import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import web2mobile from './assets/vitrine/web2mobile.jpg';

const feLog = (...args) => console.log("[StudioV2]", ...args);

const WV_DEFAULT_VIBY = {
  music_mode: "auto",      // none | auto | preset | prompt
  music_preset: "trending_pop",
  music_prompt: "",
  filter: "none",
  filter_intensity: 60,    // 0..100
  effects: [],
  beat_sync: true,
  cut_energy: 70           // 0..100
};
// ===== Built-in export templates (synced from JourneyAdmin) =====
const WV_BUILTIN_EXPORT_TEMPLATES = [
// ✅ Core / For you
  { id: 'classic', title: 'Classic', desc: 'Sequential montage', badge: 'AUTO', cat: 'for_you' },
  { id: 'grid_2x2', title: 'Grid 2×2', desc: '4 tiles on screen', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'split_v', title: 'Split Vertical', desc: '2 side by side', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'split_h', title: 'Split Horizontal', desc: '2 top / bottom', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'hero_3', title: 'Hero + 3', desc: 'Big + stacked', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'strips_3', title: '3 Strips', desc: 'Vertical bands', badge: 'LAYOUT', cat: 'for_you' },
  { id: 'corners_float', title: 'Floating Corners', desc: 'Blur bg + tiles', badge: 'LAYOUT', cat: 'for_you' },

  // ✅ FX / Ambiances (single media + vibe)
  { id: 'fx_moonrise',    title: 'Moonrise',     desc: 'Cinematic glow + default music', badge: 'FX', cat: 'fx' },
  { id: 'fx_beat_punch',  title: 'Beat Punch',   desc: 'Punchy cuts + upbeat bed',       badge: 'FX', cat: 'fx' },
  { id: 'fx_dreamy',      title: 'Dreamy',       desc: 'Soft blur + warm ambience',      badge: 'FX', cat: 'fx' },
  { id: 'fx_glitch',      title: 'Glitch',       desc: 'Digital chroma shift + noise',   badge: 'FX', cat: 'fx' },
  { id: 'fx_vhs',         title: 'VHS',          desc: 'Retro noise + vignette',         badge: 'FX', cat: 'fx' },


  // 🎧 Viby (TikTok / CapCut-like vibe presets: music + filters + effects)
  { id: 'viby_trend_pop',    title: 'Viby • Trend Pop',     desc: 'Trending pop bed + neon glow + beat hits',     badge: 'VIBY', cat: 'viby', baseId: 'fx_beat_punch',
    viby: { music_mode: 'preset', music_preset: 'trending_pop', music_prompt: '', filter: 'neon_glow', filter_intensity: 72, effects: ['beat_flash','zoom_punch','vignette'], beat_sync: true, cut_energy: 82 },
    style: { fx: 'neon', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_lofi_chill',   title: 'Viby • Lo‑Fi Chill',    desc: 'Lo‑fi chill bed + pastel dream + soft cuts',  badge: 'VIBY', cat: 'viby', baseId: 'fx_dreamy',
    viby: { music_mode: 'preset', music_preset: 'lofi_chill', music_prompt: '', filter: 'pastel_dream', filter_intensity: 68, effects: ['bokeh','film_grain'], beat_sync: false, cut_energy: 45 },
    style: { fx: 'dreamy', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_afro_sunset',  title: 'Viby • Afro Sunset',   desc: 'Afro groove + warm cinema + light leaks',     badge: 'VIBY', cat: 'viby', baseId: 'fx_fireflies',
    viby: { music_mode: 'preset', music_preset: 'afro_groove', music_prompt: '', filter: 'cinema_warm', filter_intensity: 74, effects: ['light_leaks','sparkles','vignette'], beat_sync: true, cut_energy: 70 },
    style: { fx: 'fireflies', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_edm_drop',     title: 'Viby • EDM Drop',      desc: 'Build + drop + strobe cuts + beat sync',      badge: 'VIBY', cat: 'viby', baseId: 'fx_beat_punch',
    viby: { music_mode: 'preset', music_preset: 'edm_drop', music_prompt: '', filter: 'crisp_hdr', filter_intensity: 62, effects: ['beat_flash','shake','motion_blur'], beat_sync: true, cut_energy: 92 },
    style: { fx: 'prism', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_vhs_beat',     title: 'Viby • VHS Beat',      desc: 'VHS filter + RGB split + punchy rhythm',      badge: 'VIBY', cat: 'viby', baseId: 'fx_vhs',
    viby: { music_mode: 'preset', music_preset: 'vintage_funk', music_prompt: '', filter: 'vhs', filter_intensity: 78, effects: ['rgb_split','film_grain','vignette'], beat_sync: true, cut_energy: 76 },
    style: { fx: 'vhs', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_glitch_drop',  title: 'Viby • Glitch Drop',   desc: 'Glitch FX + dark drill + sharp cuts',         badge: 'VIBY', cat: 'viby', baseId: 'fx_glitch',
    viby: { music_mode: 'preset', music_preset: 'drill_dark', music_prompt: '', filter: 'crisp_hdr', filter_intensity: 55, effects: ['rgb_split','shake','beat_flash'], beat_sync: true, cut_energy: 88 },
    style: { fx: 'glitch', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_cinematic',    title: 'Viby • Cinematic',     desc: 'Cinematic score + warm grade + slow motion',  badge: 'VIBY', cat: 'viby', baseId: 'fx_moonrise',
    viby: { music_mode: 'preset', music_preset: 'cinematic', music_prompt: '', filter: 'cinema_warm', filter_intensity: 70, effects: ['vignette','film_grain'], beat_sync: false, cut_energy: 40 },
    style: { fx: 'moonrise', music: { mode: 'auto', prompt: '' } }
  },
  { id: 'viby_minimal',      title: 'Viby • Minimal',       desc: 'Minimal piano + clean look + gentle cuts',    badge: 'VIBY', cat: 'viby', baseId: 'classic',
    viby: { music_mode: 'preset', music_preset: 'minimal_piano', music_prompt: '', filter: 'none', filter_intensity: 60, effects: [], beat_sync: false, cut_energy: 35 },
    style: { fx: 'none', music: { mode: 'auto', prompt: '' } }
  },

  // ✨ New FX variations (mapped to existing bases)
  { id: 'fx_neon_night',  title: 'Neon Night',   desc: 'Neon wash + glossy glow',        badge: 'FX', cat: 'fx', baseId: 'fx_beat_punch', style: { fx: 'neon', music: { mode: 'auto', prompt: 'neon synthwave bed, punchy, clean, 100-120bpm' } } },
  { id: 'fx_film_grain',  title: 'Film Grain',   desc: 'Soft grain + cinematic fade',    badge: 'FX', cat: 'fx', baseId: 'fx_moonrise', style: { fx: 'film',  music: { mode: 'auto', prompt: 'cinematic ambient, warm pads, subtle hits' } } },
  { id: 'fx_bokeh_burst', title: 'Bokeh Burst',  desc: 'Bokeh sparkle + smooth drift',   badge: 'FX', cat: 'fx', baseId: 'fx_dreamy',   style: { fx: 'bokeh', music: { mode: 'auto', prompt: 'dreamy lofi, gentle sparkle, slow' } } },
  { id: 'fx_prism',       title: 'Prism',        desc: 'Prismatic edges + light leaks',  badge: 'FX', cat: 'fx', baseId: 'fx_glitch',   style: { fx: 'prism', music: { mode: 'auto', prompt: 'future bass minimal, airy, upbeat' } } },
  { id: 'fx_fireflies',   title: 'Fireflies',    desc: 'Floating lights + twilight tone',badge: 'FX', cat: 'fx', baseId: 'fx_moonrise', style: { fx: 'fireflies', music: { mode: 'auto', prompt: 'evening ambient, soft piano, shimmer' } } },
  { id: 'fx_noir',        title: 'Noir',         desc: 'High contrast + noir vignette',  badge: 'FX', cat: 'fx', baseId: 'fx_vhs',      style: { fx: 'noir', music: { mode: 'auto', prompt: 'noir jazz, brushed drums, smoky, slow' } } },

  // 🎛 Backgrounds (multi‑tile with animated / image-like backdrops)
  { id: 'bg_gradient_wave', title: 'Gradient Wave',  desc: 'Animated gradient + floating tiles', badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'animated-gradient', name: 'wave' } } },
  { id: 'bg_neon_grid',     title: 'Neon Grid',      desc: 'Neon grid + modern glass tiles',   badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'neon-grid' }, fx: 'neon' } },
  { id: 'bg_particles',     title: 'Particles',      desc: 'Particle field + depth layers',     badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'particles' } } },
  { id: 'bg_blur_cover',    title: 'Blur Cover',     desc: 'Blurred cover + crisp cards',       badge: 'BG', cat: 'backgrounds', baseId: 'corners_float', style: { background: { type: 'blur-from-media', source: 'first' } } },
  { id: 'bg_paper_cut',     title: 'Paper Cut',      desc: 'Paper texture + taped frames',      badge: 'BG', cat: 'backgrounds', baseId: 'hero_3',        style: { background: { type: 'paper-texture' } } },
  { id: 'bg_sunburst',      title: 'Sunburst',       desc: 'Radial burst + punchy layout',      badge: 'BG', cat: 'backgrounds', baseId: 'grid_2x2',      style: { background: { type: 'sunburst' } } },
  { id: 'bg_glassmorphism', title: 'Glassmorphism',  desc: 'Frosted panels + soft glow',         badge: 'BG', cat: 'backgrounds', baseId: 'hero_3',        style: { background: { type: 'glass' }, fx: 'soft-glow' } },

  // 🎞 Animated (UI‑preview + motion defaults; backend remains compatible via baseId)
  { id: 'ani_carousel',     title: 'Carousel',       desc: 'Swipe rhythm + card carousel',       badge: 'ANIM', cat: 'animated', baseId: 'split_v',      style: { motion: 'capcut' } },
  { id: 'ani_flash_cards',  title: 'Flash Cards',    desc: 'Fast flips + crisp frames',          badge: 'ANIM', cat: 'animated', baseId: 'grid_2x2',     style: { motion: 'capcut', transition: 'zoom' } },
  { id: 'ani_parallax',     title: 'Parallax',       desc: 'Depth layers + slow drift',          badge: 'ANIM', cat: 'animated', baseId: 'hero_3',       style: { motion: 'subtle', fx: 'parallax' } },
  { id: 'ani_pop_stickers', title: 'Pop Stickers',   desc: 'Stickers + playful HUD',             badge: 'ANIM', cat: 'animated', baseId: 'corners_float',style: { motion: 'capcut', fx: 'stickers' } },
  { id: 'ani_comic_panels', title: 'Comic Panels',   desc: 'Panel slides + bold vibe',           badge: 'ANIM', cat: 'animated', baseId: 'strips_3',     style: { motion: 'capcut', fx: 'comic' } },
  { id: 'ani_timeline',     title: 'Timeline',       desc: 'Progress line + story beats',        badge: 'ANIM', cat: 'animated', baseId: 'hero_3',       style: { motion: 'subtle' } },
  { id: 'ani_spotlight',    title: 'Spotlight',      desc: 'Spotlight sweep + vignette',         badge: 'ANIM', cat: 'animated', baseId: 'split_h',      style: { motion: 'subtle', fx: 'spotlight' } },

  // 🧩 Collage variants (different structures, mapped to known bases)
  { id: 'collage_diagonal', title: 'Diagonal Split', desc: 'Diagonal cut + modern collage',      badge: 'LAYOUT', cat: 'for_you', baseId: 'split_v',      style: { layout: 'diagonal' } },
  { id: 'collage_stack',    title: 'Stacked Cards',  desc: 'Stacked frames + depth shadows',     badge: 'LAYOUT', cat: 'for_you', baseId: 'corners_float',style: { layout: 'stack' } },
  { id: 'collage_circle',   title: 'Circle Collage', desc: 'Circular crops + clean grid',        badge: 'LAYOUT', cat: 'for_you', baseId: 'grid_2x2',     style: { layout: 'circle' } },

  // 🪩 Retro / Editorial
  { id: 'retro_polaroid',   title: 'Polaroid Stack', desc: 'Polaroid frames + tape',             badge: 'RETRO', cat: 'retro', baseId: 'corners_float', style: { fx: 'polaroid' } },
  { id: 'retro_magazine',   title: 'Magazine',       desc: 'Editorial blocks + headlines',      badge: 'RETRO', cat: 'retro', baseId: 'hero_3',       style: { fx: 'editorial' } },
  { id: 'retro_super8',     title: 'Super 8',        desc: 'Warm flicker + film gate',          badge: 'RETRO', cat: 'retro', baseId: 'fx_vhs',       style: { fx: 'super8', music: { mode: 'auto', prompt: 'retro indie, warm, tape wow' } } },

  // "Wall" templates (single media + UI overlays)
  { id: 'wall_sale',   title: 'Sale Poster',    desc: 'Promo sticker + CTA',     badge: 'WALL', cat: 'business' },
  { id: 'wall_brand',  title: 'Brand Showcase', desc: 'Title + subtitle blocks', badge: 'WALL', cat: 'business' },
  { id: 'wall_quote',  title: 'Quote Story',    desc: 'Centered quote',          badge: 'WALL', cat: 'for_you' },
  { id: 'wall_lyrics', title: 'Lyrics Flow',    desc: 'Line-by-line text',       badge: 'WALL', cat: 'lyrics' },
  { id: 'wall_meme',   title: 'Meme Caption',   desc: 'Top/bottom captions',     badge: 'WALL', cat: 'memes' },
  { id: 'wall_retro',  title: 'Retro Frame',    desc: 'Film border + timestamp', badge: 'WALL', cat: 'retro' },
  { id: 'wall_speed',  title: 'Speed Cut',      desc: 'Bold HUD overlay',        badge: 'WALL', cat: 'speed' },

  // ✨ New wall styles
  { id: 'wall_podcast', title: 'Podcast Cover',  desc: 'Title + waveform overlay', badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'waveform' } },
  { id: 'wall_news',    title: 'News Card',      desc: 'Headline + ticker bar',    badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'ticker' } },
  { id: 'wall_recipe',  title: 'Recipe Card',    desc: 'Ingredients + badge',      badge: 'WALL', cat: 'business', baseId: 'wall_brand', style: { overlay: 'recipe' } },
  { id: 'wall_event',   title: 'Event Flyer',    desc: 'Date/time + location',     badge: 'WALL', cat: 'business', baseId: 'wall_sale',  style: { overlay: 'event' } },
];


const wvNormalizeViby = (raw) => {
  const o = (raw && typeof raw === "object") ? raw : {};
  const out = { ...WV_DEFAULT_VIBY, ...o };
  // clamp
  out.filter_intensity = Math.max(0, Math.min(100, Number(out.filter_intensity ?? 60)));
  out.cut_energy = Math.max(0, Math.min(100, Number(out.cut_energy ?? 70)));
  out.effects = Array.isArray(out.effects) ? out.effects.filter(Boolean) : [];
  out.beat_sync = !!out.beat_sync;
  out.music_mode = String(out.music_mode || "auto");
  out.music_preset = String(out.music_preset || "trending_pop");
  out.music_prompt = String(out.music_prompt || "");
  out.filter = String(out.filter || "none");
  return out;
};

const safeParseSteps = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : (parsed?.steps ? parsed.steps : []);
    } catch {
      return [];
    }
  }
  if (typeof raw === "object") {
    if (Array.isArray(raw.steps)) return raw.steps;
    return [];
  }
  return [];
};

// Prefix with /uploads when needed
const upath = (p) => {
  if (!p) return "";
  const s = String(p);
  if (s.startsWith("/uploads/")) return s;
  if (s.startsWith("http")) return s;
  if (s.startsWith("/")) return s;
  return `/uploads/${s}`;
};

const wvStripQuery = (u) => String(u || "").split(/[?#]/)[0];
const isVideoUrl = (url) => /\.(mp4|mov|webm|mkv)(\?|#|$)/i.test(wvStripQuery(url));

const WVMediaThumb = ({ src, className, placeholder }) => {
  const [broken, setBroken] = useState(false);
  const s = src || "";
  const isVid = isVideoUrl(s);

  if (!s || broken) {
    return <img className={className} src={placeholder} alt="" draggable={false} />;
  }

  if (isVid) {
  return (
    <video
      className={className}
      src={s}
      muted
      playsInline
      loop
      preload="metadata"
      draggable={false}
      onMouseEnter={(e) => {
        try {
          const v = e.currentTarget;
          // Hover-play (muted) for Thread Bank / pills
          v.play?.();
        } catch {}
      }}
      onMouseLeave={(e) => {
        try {
          const v = e.currentTarget;
          v.pause?.();
          // Keep a visible frame after hover ends
          v.currentTime = 0.01;
        } catch {}
      }}
      onLoadedData={(e) => {
        try {
          // Grab a frame quickly so the thumbnail is not blank
          if (e.currentTarget.currentTime === 0) e.currentTarget.currentTime = 0.01;
        } catch {}
      }}
      onError={() => setBroken(true)}
    />
  );
}

return <img className={className} src={s} alt="" draggable={false} onError={() => setBroken(true)} />;
};




const wvNormalizeUsername = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.startsWith("@") ? s.slice(1) : s;
};
const wvDicebearInitialsPng = (u, size = 96) =>
  `https://api.dicebear.com/7.x/initials/png?seed=${encodeURIComponent(u)}&size=${size}`;
const wvContributorUsername = (m) => {
  const raw =
    m?.contributor_username ??
    m?.contributor ??
    m?.owner_username ??
    m?.owner ??
    m?.username ??
    m?.created_by ??
    m?.createdBy ??
    m?.user_id ??
    m?.userId ??
    "";
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.startsWith("@") ? s.slice(1) : s;
};

const wvContributorAvatarUrl = (m, usersByUsername = {}) => {
  // 1) direct avatar on media record (if backend provides it)
  const direct =
    m?.contributor_avatar ||
    m?.avatar_url ||
    m?.avatarUrl ||
    m?.avatar ||
    m?.photo_url ||
    m?.picture ||
    m?.image_url ||
    "";
  if (direct) return upath(direct);

  // 2) look up avatar from /users map
  const uname = wvContributorUsername(m);
  const rec = uname ? usersByUsername?.[uname] : null;
  const fromMap = String(
    rec?.avatar_url ??
    rec?.avatarUrl ??
    rec?.photo_url ??
    rec?.picture ??
    rec?.image_url ??
    ""
  ).trim();
  if (fromMap) return upath(fromMap);

  // 3) fallback to a PNG initials (always works)
  if (!uname) return wvDicebearInitialsPng("weavevo", 96);
  return wvDicebearInitialsPng(uname, 96);
};

const toast = (msg, type = "info") => {
  try {
    (window.addToast || window.toast || window.notify)?.(msg, type);
  } catch { /* ignore */ }
};


// Download helpers
const wvEscapeXml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const wvDownloadTextFile = (filename, content, mime = "text/plain") => {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    console.error("download failed", e);
    toast("Download failed.", "error");
  }
};

function WVAvatar({ m, usersByUsername = {}, size = 20, className = "" }) {
  const src = wvContributorAvatarUrl(m, usersByUsername);
  const username = wvContributorUsername(m);
  if (!src) return null;

  const imgStyle = {
    width: "100%",
    height: "100%",
    borderRadius: 999,
    objectFit: "cover",
    border: "1px solid rgba(15,23,42,0.14)",
    background: "rgba(255,255,255,0.92)",
    display: "block",
  };

  if (username) {
    return (
      <Link
        to={`/user/${encodeURIComponent(username)}`}
        className={className}
        title={`Open @${username} profile`}
        onClick={(e) => {
          // Don't trigger thread select when clicking the avatar
          e.stopPropagation();
        }}
        style={{
          width: size,
          height: size,
          borderRadius: 999,
          overflow: "hidden",
          display: "block",
          textDecoration: "none",
        }}
      >
        <img src={src} alt="" style={imgStyle} draggable={false} />
      </Link>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      draggable={false}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        objectFit: "cover",
        border: "1px solid rgba(15,23,42,0.14)",
        background: "rgba(255,255,255,0.92)",
        display: "block",
      }}
    />
  );
}




const wvLower = (v) => String(v ?? "").trim().toLowerCase();

const wvParseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return {};
  try {
    const payload = token.split(".")[1] || "";
    if (!payload) return {};
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary =
      (typeof window !== "undefined" && typeof window.atob === "function")
        ? window.atob(padded)
        : atob(padded);
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    const json = typeof TextDecoder !== "undefined" ? new TextDecoder().decode(bytes) : binary;
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const wvCurrentIdentityCandidates = (token) => {
  const payload = wvParseJwtPayload(token);
  const email = String(payload?.email || "").trim().toLowerCase();
  const emailLocal = email.includes("@") ? email.split("@")[0] : "";

  return Array.from(
    new Set(
      [
        payload?.preferred_username,
        payload?.username,
        payload?.upn,
        payload?.sub,
        email,
        emailLocal,
      ]
        .map((v) => wvLower(wvNormalizeUsername(v)))
        .filter(Boolean)
    )
  );
};

const wvOwnerIdentityCandidates = (project, scenario) =>
  Array.from(
    new Set(
      [
        project?.creator_username,
        project?.owner_username,
        project?.created_by_username,
        project?.created_by,
        project?.createdBy,
        project?.owner,
        project?.username,
        project?.user_username,
        project?.author_username,
        project?.email,
        project?.user_email,
        project?.owner_email,
        project?.user_id,
        project?.userId,
        project?.owner_id,
        project?.ownerId,

        scenario?.creator_username,
        scenario?.owner_username,
        scenario?.created_by_username,
        scenario?.created_by,
        scenario?.createdBy,
        scenario?.owner,
        scenario?.username,
        scenario?.author_username,
        scenario?.email,
        scenario?.user_email,
        scenario?.owner_email,
        scenario?.user_id,
        scenario?.userId,
        scenario?.owner_id,
        scenario?.ownerId,
      ]
        .map((v) => wvLower(wvNormalizeUsername(v)))
        .filter(Boolean)
    )
  );


/* =========================================================================================
 * WEB SCREEN TEMPORARILY DISABLED — MOBILE-FIRST EXPERIENCE
 * The original web implementation is preserved below as JourneyStudioV2Legacy.
 * ========================================================================================= */
const WV_MOBILE_GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";
const WV_MOBILE_IOS_WAITLIST_URL = "/join-beta";
const WV_MOBILE_LANG_KEY = "weavevo_lang";

const WV_MOBILE_SCREEN_COPY = {
  admin: {
    en: {
      eyebrow: "MOBILE PROJECT MANAGEMENT",
      title: "Manage your Weave from the mobile app.",
      body: "Project setup, contributors, scenario management, moderation and delivery controls are designed for a more focused mobile workflow.",
    },
    fr: {
      eyebrow: "GESTION MOBILE DU PROJET",
      title: "Gérez votre Weave depuis l’app mobile.",
      body: "La gestion du projet, des contributeurs, du scénario, de la modération et de la livraison est optimisée pour une expérience mobile plus fluide.",
    },
  },
  studio: {
    en: {
      eyebrow: "MOBILE STUDIO",
      title: "Finish your Weave in the mobile Studio.",
      body: "Media selection, layouts, transitions, music, effects and final export are easier to shape from the mobile Studio experience.",
    },
    fr: {
      eyebrow: "STUDIO MOBILE",
      title: "Finalisez votre Weave dans le Studio mobile.",
      body: "La sélection des médias, les layouts, transitions, musiques, effets et l’export final sont conçus pour être plus fluides depuis le Studio mobile.",
    },
  },
  contributor: {
    en: {
      eyebrow: "MOBILE CONTRIBUTION",
      title: "Contribute to this Weave from your phone.",
      body: "Capture, upload, slot guidance, AI review and collaboration are designed around the device already in your hand.",
    },
    fr: {
      eyebrow: "CONTRIBUTION MOBILE",
      title: "Contribuez à cette Weave depuis votre téléphone.",
      body: "La capture, l’upload, le guidage par slot, la revue IA et la collaboration sont pensés autour du mobile.",
    },
  },
};

export default function JourneyStudioV2() {
  const [wvMobileLang, setWvMobileLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      return String(raw).toLowerCase().startsWith("fr") ? "fr" : "en";
    } catch (_) {
      return "en";
    }
  });

  useEffect(() => {
    const refreshLang = () => {
      try {
        const raw = localStorage.getItem(WV_MOBILE_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        setWvMobileLang(String(raw).toLowerCase().startsWith("fr") ? "fr" : "en");
      } catch (_) { }
    };
    window.addEventListener("storage", refreshLang);
    window.addEventListener("focus", refreshLang);
    return () => {
      window.removeEventListener("storage", refreshLang);
      window.removeEventListener("focus", refreshLang);
    };
  }, []);

  const baseCopy = WV_MOBILE_SCREEN_COPY.studio[wvMobileLang] || WV_MOBILE_SCREEN_COPY.studio.en;
  const labels = wvMobileLang === "fr"
    ? {
        google: "Télécharger sur Google Play",
        apple: "App Store / iOS",
        soon: "Bientôt disponible",
      }
    : {
        google: "Get it on Google Play",
        apple: "App Store / iOS",
        soon: "Coming soon",
      };

  return (
    <div className="wv-mobile-screen-gate">
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-a" aria-hidden="true" />
      <div className="wv-mobile-screen-orb wv-mobile-screen-orb-b" aria-hidden="true" />

      <section className="wv-mobile-screen-card" aria-labelledby="wv-mobile-screen-title">
        <div className="wv-mobile-screen-visual">
          <img
            src={web2mobile}
            alt="Weavevo web and mobile experience"
            className="wv-mobile-screen-visual-img"
          />
        </div>

        <div className="wv-mobile-screen-copy">
          <span className="wv-mobile-screen-eyebrow">{baseCopy.eyebrow}</span>
          <h1 id="wv-mobile-screen-title">{baseCopy.title}</h1>
          <p className="wv-mobile-screen-lead">{baseCopy.body}</p>


          <div className="wv-mobile-screen-actions">
            <a
              className="wv-mobile-screen-store is-google"
              href={WV_MOBILE_GOOGLE_PLAY_URL}
              target="_blank"
              rel="noreferrer"
              aria-label={labels.google}
            >
              <i className="bi bi-google-play" />
              <span>{labels.google}</span>
            </a>

            <a
              className="wv-mobile-screen-store is-apple"
              href={WV_MOBILE_IOS_WAITLIST_URL}
              aria-label={`${labels.apple} — ${labels.soon}`}
            >
              <i className="bi bi-apple" />
              <span>
                <strong>{labels.apple}</strong>
                <small>{labels.soon}</small>
              </span>
            </a>
          </div>

        </div>
      </section>

      <style>{`
        .wv-mobile-screen-gate,
        .wv-mobile-screen-gate * { box-sizing: border-box; }
        .wv-mobile-screen-gate {
          position: relative;
          min-height: calc(100vh - 70px);
          width: 100%;
          overflow: hidden;
          display: grid;
          place-items: center;
          padding: clamp(28px, 5vw, 72px);
          background:
            radial-gradient(circle at 18% 18%, rgba(70,154,255,.14), transparent 32%),
            radial-gradient(circle at 82% 74%, rgba(139,92,246,.12), transparent 34%),
            linear-gradient(180deg, #fbfdff 0%, #f5f8ff 100%);
          color: #08111e;
        }
        .wv-mobile-screen-orb {
          position: absolute;
          width: 340px;
          height: 340px;
          border-radius: 50%;
          filter: blur(60px);
          opacity: .32;
          pointer-events: none;
        }
        .wv-mobile-screen-orb-a { background:#5ab7ff; top:-150px; left:-80px; }
        .wv-mobile-screen-orb-b { background:#8b5cf6; right:-120px; bottom:-170px; }
        .wv-mobile-screen-card {
          position: relative;
          z-index: 1;
          width: min(1040px, 100%);
          min-height: 560px;
          display: grid;
          grid-template-columns: minmax(260px,.78fr) minmax(0,1.22fr);
          align-items: center;
          gap: clamp(34px,6vw,86px);
          padding: clamp(34px,5vw,70px);
          border: 1px solid rgba(10,32,70,.09);
          border-radius: 34px;
          background: rgba(255,255,255,.91);
          box-shadow: 0 35px 100px rgba(21,53,95,.12);
          backdrop-filter: blur(18px);
        }
        .wv-mobile-screen-visual {
          width:min(360px,82vw);
          justify-self:center;
          border-radius:28px;
          overflow:hidden;
          background:#eef3f8;
          box-shadow:0 28px 64px rgba(8,19,37,.18);
          transform:rotate(-1.5deg);
        }
        .wv-mobile-screen-visual-img {
          display:block;
          width:100%;
          height:auto;
          object-fit:cover;
        }
        .wv-mobile-screen-copy { min-width:0; }
        .wv-mobile-screen-eyebrow {
          display:inline-flex;
          padding:7px 11px;
          border-radius:999px;
          background:#eef8ff;
          color:#087ea4;
          font-size:12px;
          font-weight:850;
          letter-spacing:.13em;
        }
        .wv-mobile-screen-copy h1 {
          margin:18px 0;
          max-width:720px;
          font-size:clamp(2.5rem,5vw,4.8rem);
          line-height:.98;
          letter-spacing:-.055em;
          color:#07111f;
        }
        .wv-mobile-screen-lead {
          max-width:700px;
          margin:0;
          color:#415169;
          font-size:clamp(1rem,1.45vw,1.17rem);
          line-height:1.72;
        }
        .wv-mobile-screen-actions { display:flex; flex-wrap:wrap; gap:12px; margin-top:28px; }
        .wv-mobile-screen-store {
          min-height:58px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:11px;
          padding:10px 18px;
          border-radius:16px;
          text-decoration:none!important;
          font-weight:800;
          transition:transform .18s ease, box-shadow .18s ease;
        }
        .wv-mobile-screen-store:hover { transform:translateY(-2px); }
        .wv-mobile-screen-store > i { font-size:1.5rem; }
        .wv-mobile-screen-store.is-google {
          background:#08111e;
          color:#fff;
          box-shadow:0 14px 28px rgba(8,17,30,.18);
        }
        .wv-mobile-screen-store.is-apple {
          border:1px solid #d8e1ec;
          background:#fff;
          color:#08111e;
        }
        .wv-mobile-screen-store.is-apple span { display:grid; line-height:1.15; }
        .wv-mobile-screen-store.is-apple small {
          margin-top:3px;
          color:#6f7d91;
          font-size:10px;
          font-weight:700;
        }
        @media (max-width:820px) {
          .wv-mobile-screen-gate { padding:22px 16px; }
          .wv-mobile-screen-card {
            grid-template-columns:1fr;
            padding:30px 22px;
            border-radius:26px;
            text-align:center;
          }
          .wv-mobile-screen-visual { width:min(330px,88vw); transform:none; }
          .wv-mobile-screen-copy h1,
          .wv-mobile-screen-lead { margin-left:auto; margin-right:auto; }
          .wv-mobile-screen-actions { justify-content:center; }
        }
      `}</style>
    </div>
  );
}

/* Original web implementation preserved below. */

function JourneyStudioV2Legacy() {

  const { id } = useParams();
  const { token } = useAuth();

  // Avatars source (Keycloak / backend)
  const [usersByUsername, setUsersByUsername] = useState({});

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch('/users', { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const arr = await r.json();
        const map = {};
        (arr || []).forEach((u) => { if (u?.username) map[u.username] = u; });
        setUsersByUsername(map);
      } catch {
        /* silent */
      }
    })();
  }, [token]);

  // Data
  const [project, setProject] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [scenarioForm, setScenarioForm] = useState({ title: "", description: "", steps: [] });

  const [projectMedia, setProjectMedia] = useState([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  // UI state
  const [tab, setTab] = useState("all"); // all | approved | rejected
  const [q, setQ] = useState("");
  const [activeStepKey, setActiveStepKey] = useState(null); // stepId or 'no-step'
  const [activeMediaId, setActiveMediaId] = useState(null);
  const didAutoSelectStepRef = useRef(false);
  const didHydrateFromMontageRef = useRef(false);
  const selectionAutosaveRef = useRef({ timer: null, lastSig: "", didInit: false });


  const [dragOverStepKey, setDragOverStepKey] = useState(null);

  // Montage + export state (same keys as JourneyAdmin)
  const [selectedMontageId, setSelectedMontageId] = useState(null);
  const [restoredMontageId] = useState(null);
  const [reloadDraftsTick, setReloadDraftsTick] = useState(0); // kept for compatibility (no UI here)
  const loadMontageDrafts = async () => { /* noop in StudioV2 */ };

  const [montageMode, setMontageMode] = useState("IA");
  const [theme, setTheme] = useState("Documentaire");

  const [voiceOverFile, setVoiceOverFile] = useState(null);
  const [voiceOverFileName, setVoiceOverFileName] = useState(null);
  const [musicFile, setMusicFile] = useState(null);
  const [musicFileName, setMusicFileName] = useState(null);
  const [audioMixOption, setAudioMixOption] = useState("voice+music");
  const [musicVolume, setMusicVolume] = useState(20);

  const [voiceMode, setVoiceMode] = useState("global");
  const [perStepVoiceMap, setPerStepVoiceMap] = useState({});
  const [transition, setTransition] = useState("none");

  const [busy, setBusy] = useState(false);
  const [finalVideoUrl, setFinalVideoUrl] = useState("");

  // Preview modes: contribution vs montage (template-based)
  const [previewMode, setPreviewMode] = useState("contribution"); // contribution | montage


  // Preview zoom (click preview to view at full size)
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomUrl, setZoomUrl] = useState("");
  const [zoomIsVideo, setZoomIsVideo] = useState(false);

  const openZoom = (url) => {
    if (!url) return;
    setZoomUrl(url);
    setZoomIsVideo(isVideoUrl(url));
    setZoomOpen(true);
  };

  const closeZoom = () => setZoomOpen(false);

  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen]);


// Layout (optimize for small 14" screens without breaking 17")
const topbarRef = useRef(null);
const slotlineRef = useRef(null);

const previewCardRef = useRef(null);
const [syncedPreviewH, setSyncedPreviewH] = useState(null);

const [viewport, setViewport] = useState(() => {
  if (typeof window === "undefined") return { w: 1920, h: 1080 };
  return { w: window.innerWidth || 1920, h: window.innerHeight || 1080 };
});
const isSmall14 = (viewport.w <= 1450 && viewport.h <= 900);

const [settingsOpen, setSettingsOpen] = useState(false);
const [settingsCollapsed, setSettingsCollapsed] = useState(false);
const [settingsTab, setSettingsTab] = useState("templates");

useEffect(() => {
  if (isSmall14) {
    setSettingsCollapsed(false);
  } else {
    setSettingsOpen(false);
  }
}, [isSmall14]);



useEffect(() => {
  if (typeof window === "undefined") return;
  if (isSmall14 || settingsCollapsed) {
    setSyncedPreviewH(null);
    return;
  }
  const el = previewCardRef.current;
  if (!el) return;

  const clampH = (h) => {
    const max = (window.innerHeight || h) - 160;
    const min = 260;
    return Math.max(min, Math.min(h, max));
  };

  const update = () => {
    const rect = el.getBoundingClientRect();
    const h = rect?.height || el.offsetHeight || 0;
    if (!h) return;
    const next = Math.round(clampH(h));
    setSyncedPreviewH((prev) => (prev && Math.abs(prev - next) < 2 ? prev : next));
  };

  update();

  let ro;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => update());
    ro.observe(el);
  }

  window.addEventListener("resize", update);
  return () => {
    window.removeEventListener("resize", update);
    if (ro) ro.disconnect();
  };
}, [isSmall14, settingsCollapsed]);



const [slotlineCollapsed, setSlotlineCollapsed] = useState(false);
const [layoutVars, setLayoutVars] = useState({ topbarH: 64, slotlineH: 220, fixedLeft: 14, fixedRight: 14 });

useEffect(() => {
  const onResize = () => {
    try {
      setViewport({ w: window.innerWidth || 1920, h: window.innerHeight || 1080 });
    } catch { /* ignore */ }
  };
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

useEffect(() => {
  // Measure dynamic heights so the editor fits above the Slot Line on 14"
  const measure = () => {
    const th = topbarRef.current?.offsetHeight || 64;
    const sh = slotlineRef.current?.offsetHeight || (slotlineCollapsed ? 64 : 220);
    const rect = topbarRef.current?.getBoundingClientRect?.() || null;
    const fixedLeft = rect ? Math.round(rect.left) : (14 + (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--wv-app-left-offset')) || 0));
    const fixedRight = rect ? Math.round((window.innerWidth || viewport.w || 0) - rect.right) : 14;
    setLayoutVars({ topbarH: th, slotlineH: sh, fixedLeft, fixedRight });
  };
  measure();
  window.addEventListener("resize", measure);
  const t = setTimeout(measure, 60);
  return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
}, [slotlineCollapsed, viewport.w, viewport.h, finalVideoUrl]);

  // Template settings (based on existing export pipeline)
  const [dbExportTemplates, setDbExportTemplates] = useState([]);
  const [exportTemplate, setExportTemplate] = useState("classic");
  const [vibyOverrides, setVibyOverrides] = useState({});
  const [vibyDraft, setVibyDraft] = useState(WV_DEFAULT_VIBY);

  // Templates panel (JourneyAdmin-like previews)
  const [tplOpen, setTplOpen] = useState(true);
  const [tplCat, setTplCat] = useState("ALL");
  const [tplQuery, setTplQuery] = useState("");

  const [exportPreset, setExportPreset] = useState("youtube_1080p_16_9");
  const [exportRes, setExportRes] = useState("1920x1080");
  const [exportFPS, setExportFPS] = useState(30);
  const [exportFormat, setExportFormat] = useState("mp4");
  const [exportBitrate, setExportBitrate] = useState("6M");
  const [exportWatermarkText, setExportWatermarkText] = useState("");
  const [exportAspect, setExportAspect] = useState("16:9");
  const [exportMediaOrder] = useState([]); // kept for compatibility (we use selectedMediaIds as default order)
  const [exportMotion] = useState("capcut");

  // Helpers
  const authHeaders = () => {
    const t = localStorage.getItem("kc_token") || localStorage.getItem("token") || token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const normalizeId = (mid) => {
    const n = Number(mid);
    return Number.isFinite(n) ? n : mid;
  };

  const isSelected = (mid) => (selectedMediaIds || []).some((x) => String(x) === String(mid));

  const normalizeUploadsUrl = (val) => {
    if (!val) return null;
    const s = String(val);
    if (/^https?:\/\//.test(s)) return s;
    if (s.startsWith("/uploads/")) return s;
    if (s.startsWith("/")) return s;
    return upath(s);
  };

  // Keep StudioV2 aligned with JourneyAdmin: restore the latest montage config (incl. media order) when possible.
  const reloadMontageConfigs = useCallback(async () => {
    if (!id) return;
    try {
      const r = await fetch(`/projects/${id}/montage-configs`, {
        headers: { Accept: "application/json", ...authHeaders() }
      });
      if (!r.ok) return;
      const json = await r.json().catch(() => ([]));
      const list = Array.isArray(json) ? json : [];
      if (!list.length) return;

      // Take most recent montage (backend already returns latest first)
      const latest = list[0];

      if (latest?.id != null) setSelectedMontageId(latest.id);

      // Basic export knobs
      if (latest.transition) setTransition(latest.transition);
      if (latest.audio_mix_option) setAudioMixOption(latest.audio_mix_option);
      if (latest.music_volume !== undefined && latest.music_volume !== null) {
        setMusicVolume(Math.round((Number(latest.music_volume) || 0) * 100));
      }

      // Voice / music files (restore server filename when possible)
      if (latest.voice_file) {
        const v = normalizeUploadsUrl(latest.voice_file);
        if (v && v.startsWith("/uploads/")) setVoiceOverFileName(v.replace("/uploads/", ""));
        else setVoiceOverFileName(null);
      }
      if (latest.music_file) {
        const mu = normalizeUploadsUrl(latest.music_file);
        if (mu && mu.startsWith("/uploads/")) setMusicFileName(mu.replace("/uploads/", ""));
        else setMusicFileName(null);
      }

      // Voice mode + per-step voice map
      const vm = latest.voice_mode || latest.voice_over_type || "global";
      setVoiceMode(vm === "per_step" || vm === "per-step" ? "per-step" : "global");

      if (latest.voice_by_step) {
        try {
          const raw =
            typeof latest.voice_by_step === "string"
              ? JSON.parse(latest.voice_by_step)
              : latest.voice_by_step;

          if (raw && typeof raw === "object") {
            const normalized = {};
            Object.entries(raw).forEach(([stepId, val]) => {
              const u = normalizeUploadsUrl(val);
              if (u) normalized[String(stepId)] = u;
            });
            setPerStepVoiceMap(normalized);
          } else {
            setPerStepVoiceMap({});
          }
        } catch {
          setPerStepVoiceMap({});
        }
      } else {
        setPerStepVoiceMap({});
      }

      // Media order (this is what JourneyAdmin uses to restore selection)
      if (latest.media_order) {
        try {
          const order =
            typeof latest.media_order === "string"
              ? JSON.parse(latest.media_order)
              : latest.media_order;

          if (Array.isArray(order)) {
            didHydrateFromMontageRef.current = true;
            setSelectedMediaIds(order.map(normalizeId));
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      console.error("reloadMontageConfigs error:", e);
    }
  }, [id, token]);

  useEffect(() => { reloadMontageConfigs(); }, [reloadMontageConfigs, reloadDraftsTick]);

  // Keep local media "selected" flag aligned with the selection array (UI consistency)
  useEffect(() => {
    const setSel = new Set((selectedMediaIds || []).map((x) => String(x)));
    setProjectMedia((prev) => {
      if (!Array.isArray(prev)) return prev;
      let changed = false;
      const next = prev.map((m) => {
        if (!m) return m;
        const mid = m?.id ?? m?.media_id ?? m?.mediaId;
        if (mid == null) return m;
        const sel = setSel.has(String(mid)) ? 1 : 0;
        if ((m?.selected ?? 0) === sel) return m;
        changed = true;
        return { ...m, selected: sel };
      });
      return changed ? next : prev;
    });
  }, [selectedMediaIds]);

  // Auto-persist selection (Approved/Used) to the same backend endpoint as JourneyAdmin,
  // so Export (and JourneyAdmin) always sees the same media selection.
  const postSelectionToBackend = useCallback(async (ids) => {
    try {
      await fetch(`/projects/${id}/media/selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ selectedMediaIds: Array.isArray(ids) ? ids : [], message: "" }),
      });
    } catch (e) {
      console.error("selection autosave error:", e);
    }
  }, [id, token]);

  useEffect(() => {
    if (!id) return;

    const sig = JSON.stringify((selectedMediaIds || []).map((x) => String(x)));

    // don't autosave on first hydration
    if (!selectionAutosaveRef.current.didInit) {
      selectionAutosaveRef.current.didInit = true;
      selectionAutosaveRef.current.lastSig = sig;
      return;
    }

    if (sig === selectionAutosaveRef.current.lastSig) return;

    if (selectionAutosaveRef.current.timer) clearTimeout(selectionAutosaveRef.current.timer);

    selectionAutosaveRef.current.timer = setTimeout(() => {
      selectionAutosaveRef.current.lastSig = sig;
      postSelectionToBackend(selectedMediaIds || []);
    }, 450);

    return () => {
      if (selectionAutosaveRef.current.timer) {
        clearTimeout(selectionAutosaveRef.current.timer);
        selectionAutosaveRef.current.timer = null;
      }
    };
  }, [id, selectedMediaIds, postSelectionToBackend]);


  const mediaById = useMemo(() => {
    const map = {};
    (projectMedia || []).forEach((m) => {
      if (!m) return;
      const mid = m.id ?? m.media_id ?? m.mediaId;
      if (!mid) return;
      map[String(mid)] = m;
    });
    return map;
  }, [projectMedia]);

  const mediaUrlById = (mid) => {
    const m = mediaById[String(mid)];
    if (!m) return "";
    const p = m.thumb_url || m.preview_url || m.file_path || m.path || m.location || m.url;
    return upath(p);
  };

  const stepById = useMemo(() => {
    const map = {};
    (scenarioForm.steps || []).forEach((s) => {
      if (!s) return;
      map[String(s.id)] = s;
    });
    return map;
  }, [scenarioForm.steps]);

  const mediaInStep = useMemo(() => {
    const map = {};
    (projectMedia || []).forEach((m) => {
      const k = (m?.step_id == null) ? "no-step" : String(m.step_id);
      if (!map[k]) map[k] = [];
      map[k].push(m);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (Number(b?.created_at_ts || 0) - Number(a?.created_at_ts || 0))));
    return map;
  }, [projectMedia]);

  // Load project + scenario + media
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const r = await fetch(`/projects/${id}`);
        const j = await r.json().catch(() => ({}));
        if (r.ok) setProject(j);
      } catch { /* ignore */ }
    })();

    // Scenario (array OR single)
    (async () => {
      try {
        const r = await fetch(`/projects/${id}/scenarios`);
        if (!r.ok) throw new Error("scenarios fetch");
        const data = await r.json().catch(() => null);

        const sc = Array.isArray(data) ? (data[0] || {}) : (data || {});
        setScenario(sc);

        const steps = safeParseSteps(sc.steps).map((st, i) => ({ id: st.id ?? i + 1, ...st }));
        setScenarioForm({
          title: sc.title || "",
          description: sc.description || "",
          steps
        });
      } catch {
        try {
          const r2 = await fetch(`/projects/${id}/scenario`);
          if (!r2.ok) throw new Error("scenario fetch");
          const sc = await r2.json().catch(() => ({}));
          setScenario(sc);

          const steps = safeParseSteps(sc.steps).map((st, i) => ({ id: st.id ?? i + 1, ...st }));
          setScenarioForm({
            title: sc.title || "",
            description: sc.description || "",
            steps
          });
        } catch { /* ignore */ }
      }
    })();

    // Media
    fetch(`/projects/${id}/media`)
      .then((res) => res.json())
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setProjectMedia(list);
        // If we already restored a precise order from montage-configs, don't overwrite it here.
        if (!didHydrateFromMontageRef.current) {
          setSelectedMediaIds(list.filter((m) => m?.selected === 1).map((m) => m.id));
        }
      })
      .catch(() => { /* ignore */ });

  }, [id]);

  // Load DB export templates (same endpoint used by JourneyAdmin)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/export-templates`, { headers: { ...authHeaders() } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const rows = await r.json().catch(() => []);
        const list = (Array.isArray(rows) ? rows : []).map((t) => ({
          id: t.id,
          label: t.title || t.name || t.id,
          description: t.description || "",
          category: t.category || "PRIVATE",
          _raw: t,
          __db: true
        }));
        setDbExportTemplates(list);
      } catch {
        setDbExportTemplates([]);
      }
    })();
  }, [id, token]);

  const allTemplates = useMemo(() => {
        const builtIn = (Array.isArray(WV_BUILTIN_EXPORT_TEMPLATES) ? WV_BUILTIN_EXPORT_TEMPLATES : []).map((x) => ({
          id: x.id,
          label: x.title || x.label || x.name || x.id,
          description: x.desc || x.description || "",
          category: x.cat || x.category || "BUILT-IN",
          badge: x.badge,
          baseId: x.baseId,
          style: x.style,
          _raw: x,
          __db: false,
          __builtin: true
        }));
        const base = builtIn.length ? builtIn : [{ id: "classic", label: "Classic", description: "Default Weavevo template", category: "BUILT-IN", __db: false, __builtin: true }];
    const db = Array.isArray(dbExportTemplates) ? dbExportTemplates : [];
    const seen = new Set();
    const out = [];
    [...base, ...db].forEach((t) => {
      if (!t?.id) return;
      if (seen.has(String(t.id))) return;
      seen.add(String(t.id));
      out.push(t);
    });
    return out;
  }, [dbExportTemplates]);

  const selectedTemplateObj = useMemo(() => {
    return (allTemplates || []).find((t) => String(t.id) === String(exportTemplate)) || null;
  }, [allTemplates, exportTemplate]);

  const getTplCat = useCallback((t) => {
    const c = t?.category ?? t?._raw?.category ?? t?._raw?.visibility ?? "OTHER";
    const s = String(c || "OTHER").trim();
    if (!s) return "OTHER";
    return s.toUpperCase();
  }, []);

  const templateCats = useMemo(() => {
    const set = new Set();
    (allTemplates || []).forEach((t) => {
      if (!t) return;
      const c = getTplCat(t);
      if (c) set.add(c);
    });
    // Prefer common JourneyAdmin-like ordering if present
    const preferred = ["PUBLIC", "PRIVATE", "IA-GENERATED", "BUILT-IN", "OTHER"];
    const arr = Array.from(set);
    arr.sort((a, b) => {
      const ia = preferred.indexOf(a);
      const ib = preferred.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
    return arr;
  }, [allTemplates, getTplCat]);

  const filteredTemplates = useMemo(() => {
    const q = (tplQuery || "").trim().toLowerCase();
    return (allTemplates || []).filter((t) => {
      if (!t) return false;
      if (tplCat && tplCat !== "ALL") {
        if (getTplCat(t) !== tplCat) return false;
      }
      if (!q) return true;
      const hay = `${t.label || ""} ${t.description || ""} ${t.id || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allTemplates, tplCat, tplQuery, getTplCat]);

  // Persist selection for JourneyAdmin (export step)
  useEffect(() => {
    if (!id) return;
    try {
      const payload = {
        selectedMediaIds: Array.isArray(selectedMediaIds) ? selectedMediaIds.map((x) => String(x)) : [],
        exportTemplate: exportTemplate ? String(exportTemplate) : "classic",
        updatedAt: Date.now()
      };
      localStorage.setItem(`wv_studio_selection_${id}`, JSON.stringify(payload));
    } catch (e) { /* ignore */ }
  }, [id, selectedMediaIds, exportTemplate]);


  // Initialize slot selection (auto-load first slot once steps are available)
useEffect(() => {
  const steps = Array.isArray(scenarioForm.steps) ? scenarioForm.steps : [];

  // First render: keep UI stable even before steps arrive
  if (activeStepKey == null && !steps.length) {
    setActiveStepKey("no-step");
    return;
  }

  // If we started on "no-step" because steps weren't loaded yet, switch to first slot once ready
  if (!didAutoSelectStepRef.current && steps.length && (activeStepKey == null || activeStepKey === "no-step")) {
    const firstStepId = steps[0]?.id;
    if (firstStepId != null) {
      didAutoSelectStepRef.current = true;
      setActiveStepKey(String(firstStepId));
    }
  }
}, [scenarioForm.steps, activeStepKey]);

// Keep active media in sync with selected slot (prefer "used" threads)
useEffect(() => {
  const key = activeStepKey || "no-step";
  const inSlot = mediaInStep[key] || [];
  const usedSet = new Set((selectedMediaIds || []).map((x) => String(x)));

  const preferred = inSlot.find((m) => usedSet.has(String(m?.id ?? m?.media_id)));
  const nextId = (preferred?.id ?? preferred?.media_id ?? inSlot[0]?.id ?? inSlot[0]?.media_id) ?? null;

  setActiveMediaId(nextId);
}, [activeStepKey, mediaInStep, selectedMediaIds]);

// Thread bank filtering
  const bankItems = useMemo(() => {
    const ql = (q || "").trim().toLowerCase();
    const all = Array.isArray(projectMedia) ? projectMedia : [];

    let items = all;

    // FILTER BY SELECTED SLOT (Thread Bank updates on slot click)
    if (activeStepKey != null) {
      const sk = String(activeStepKey);
      items = items.filter((mm) => {
        const k = (mm?.step_id == null) ? "no-step" : String(mm.step_id);
        return k === sk;
      });
    }

    // tab mapping (best-effort without adding new DB fields)
    if (tab === "approved") {
      items = items.filter((m) => isSelected(m.id));
    } else if (tab === "rejected") {
      items = items.filter((m) => !isSelected(m.id));
    }

    if (ql) {
      items = items.filter((m) => {
        const t = String(m.title || m.name || "").toLowerCase();
        const loc = String(m.location || m.place || "").toLowerCase();
        const desc = String(m.description || "").toLowerCase();
        const user = String(m.contributor_username || m.username || m.created_by || "").toLowerCase();
        return t.includes(ql) || loc.includes(ql) || desc.includes(ql) || user.includes(ql);
      });
    }

    // sort: selected first, then newest
    return items.sort((a, b) => {
      const as = isSelected(a.id) ? 1 : 0;
      const bs = isSelected(b.id) ? 1 : 0;
      if (as !== bs) return bs - as;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  }, [projectMedia, q, tab, selectedMediaIds, activeStepKey]);

  // ---- Drag & Drop (slot assignment) ----
  const onThreadDragStart = (e, m) => {
    try {
      const payload = {
        source: "project",
        media_id: m.media_id ?? null,
        linked_id: m.id,
        from_step: m.step_id ?? null
      };
      e.dataTransfer.setData("application/json", JSON.stringify(payload));
      e.dataTransfer.effectAllowed = "move";
    } catch { /* ignore */ }
  };

  const onDragOverStep = (stepKey) => (e) => {
    e.preventDefault();
    setDragOverStepKey(stepKey);
  };

  const onDropToStep = (stepKey) => async (e) => {
    e.preventDefault();
    setDragOverStepKey(null);

    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
    } catch {
      data = {};
    }

    const targetStepId = stepKey === "no-step" ? null : Number(stepKey);
    const linkedId = data.linked_id != null ? Number(data.linked_id) : null;
    const fromStep = data.from_step ?? null;
    if (!linkedId) return;

    const sourceKey = fromStep == null ? "no-step" : String(fromStep);
    if (sourceKey === stepKey) return;

    try {
      await fetch(`/linkedMedia/${linkedId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ step_id: targetStepId }),
      });

      const rows = await fetch(`/projects/${id}/media`).then((r) => r.json());
      setProjectMedia(Array.isArray(rows) ? rows : []);
      toast("Média déplacé vers la nouvelle étape.", "success");
    } catch (err) {
      console.error("Erreur déplacement média:", err);
      toast("Erreur lors du déplacement du média.", "error");
    }
  };

  // ---- Selection (Approved/Used) ----
  const toggleUse = (mid) => {
    const nid = normalizeId(mid);
    setSelectedMediaIds((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const exists = arr.some((x) => String(x) === String(nid));
      if (exists) return arr.filter((x) => String(x) !== String(nid));
      return [...arr, nid];
    });
  };

  // ==== SAME business function (copied from JourneyAdmin.js) ====
  // eslint-disable-next-line no-unused-vars
  const saveSelectionAndMontage = async () => {
    try {
      const cleanSelected = Array.isArray(selectedMediaIds) ? selectedMediaIds.map(normalizeId) : [];
      await fetch(`/projects/${id}/media/selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ selectedMediaIds: cleanSelected, message: "" }),
      });

      const fd = new FormData();
      const computedVoiceOverType = voiceOverFile ? "file" : (voiceOverFileName?.trim() ? "ia" : "none");
      fd.append("montage_type", montageMode || "");
      fd.append("theme", theme || "");
      fd.append("voice_over_type", computedVoiceOverType);
      if (voiceOverFile) fd.append("voice_over", voiceOverFile);
      if (voiceOverFileName) fd.append("voice_file", voiceOverFileName);
      if (musicFile) fd.append("music", musicFile);
      if (musicFileName) fd.append("music_file", musicFileName); // keep previous music
      fd.append("media_order", JSON.stringify(selectedMediaIds || []));
      fd.append("audio_mix_option", audioMixOption);
      fd.append("music_volume", String(musicVolume));
      fd.append("transition", transition);

      fd.append("voice_mode", voiceMode === "per-step" ? "per_step" : "global");
      if (voiceMode === "per-step") {
        const map = {};
        for (const [stepId, info] of Object.entries(perStepVoiceMap || {})) {
          if (!info) continue;
          if (info.serverPath) map[stepId] = info.serverPath;
          else if (info.fileName) map[stepId] = `/uploads/${info.fileName}`;
          else if (info.url) map[stepId] = info.url;
          else if (typeof info === "string") map[stepId] = info;
        }
        fd.append("voice_by_step", JSON.stringify(map));
      } else {
        fd.append("voice_by_step", "{}");
      }

      const res = await fetch(`/projects/${id}/save-montage`, { method: "POST", headers: { ...authHeaders() }, body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `save montage failed ${res.status}`);

      const savedId = Number(json?.id ?? json?.montage_id ?? json?.montageId ?? 0);
      if (Number.isFinite(savedId) && savedId > 0) setSelectedMontageId(savedId);

      toast("Montage sauvegardé.", "success");

      if (typeof loadMontageDrafts === "function") await loadMontageDrafts();
      setReloadDraftsTick((t) => t + 1);

      return savedId;
    } catch (e) {
      console.error("saveSelectionAndMontage error:", e);
      toast("Erreur lors de la sauvegarde du montage.", "error");
      return null;
    }
  };

  // ==== Generate Final Video (same endpoint/payload keys as JourneyAdmin) ====
  const generateFinalVideo = async () => {
    let chosenMontageId = Number(restoredMontageId ?? selectedMontageId);

    feLog("Export -> chosenMontageId =", chosenMontageId);

    if (!Number.isFinite(chosenMontageId)) {
      const savedId = await saveSelectionAndMontage();
      chosenMontageId = Number(savedId ?? restoredMontageId ?? selectedMontageId);
      feLog("Export -> chosenMontageId (after auto-save) =", chosenMontageId);
    }

    if (!Number.isFinite(chosenMontageId)) {
      toast("Sauvegardez un montage avant de générer.", "warning");
      return;
    }

    setBusy(true);
    setPreviewMode("montage");
    setFinalVideoUrl("");

    try {
      const effectiveMediaOrder = (
        Array.isArray(exportMediaOrder) && exportMediaOrder.length
          ? exportMediaOrder
          : (Array.isArray(selectedMediaIds) ? selectedMediaIds : [])
      );

      // Credits payload (safe + minimal)
      const ids = new Set((effectiveMediaOrder || []).map((x) => String(x)));
      const seen = new Set();
      const creditsContributors = [];
      const creditsContributorsDetails = [];

      const normalizeU = (raw) => {
        const s = String(raw || "").trim();
        if (!s) return "";
        return s.startsWith("@") ? s.slice(1) : s;
      };
      const dicebearPng = (u) =>
        `https://api.dicebear.com/7.x/initials/png?seed=${encodeURIComponent(u)}&size=256`;

      (projectMedia || []).forEach((m) => {
        const mid = m?.id ?? m?.media_id ?? m?.mediaId;
        if (ids.size && !ids.has(String(mid))) return;

        const uname = normalizeU(
          m?.contributor_username ??
          m?.contributor ??
          m?.username ??
          m?.created_by ??
          m?.user_id ??
          ""
        );
        if (!uname) return;

        const handle = `@${uname}`;
        const key = handle.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);

        const avatar = dicebearPng(uname);
        creditsContributors.push(handle);
        creditsContributorsDetails.push({ username: handle, avatar });
      });

      const effectiveViby = wvNormalizeViby((vibyOverrides && vibyOverrides[exportTemplate]) || vibyDraft || WV_DEFAULT_VIBY);

      const payload = {
        montage_id: chosenMontageId,
        audio_mix_option: audioMixOption,
        music_volume: (musicVolume ?? 20) / 100,
        selected_media_ids: Array.isArray(effectiveMediaOrder) ? effectiveMediaOrder : [],
        media_order: Array.isArray(effectiveMediaOrder) ? effectiveMediaOrder : [],
        transition,

        // Advanced editor state (optional)
        twick_state: null,

        voice_mode: (voiceMode === "per-step" ? "per_step" : "global"),
        voice_by_step: (voiceMode === "per-step" ? (perStepVoiceMap || null) : null),

        export_preset: exportPreset,
        export_res: exportRes,
        export_fps: exportFPS,
        export_format: exportFormat,
        export_bitrate: exportBitrate,
        export_watermark_text: exportWatermarkText,
        export_aspect: exportAspect,

        export_template: exportTemplate,
        export_template_variant: exportTemplate,
        export_template_base: null,
        export_template_style: null,

        export_viby: effectiveViby,
        export_motion: exportMotion,

        credits_enabled: true,
        credits_contributors: creditsContributors,
        credits_contributors_details: creditsContributorsDetails
      };

      feLog("POST /projects/:id/generate-final-video payload ->", payload);

      const res = await fetch(`/projects/${id}/generate-final-video`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
        credentials: "same-origin",
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);

      const url = json.videoUrl || json.url || "";
      if (url) {
        setFinalVideoUrl(url);
        toast("Vidéo générée ✅", "success");
      } else {
        toast("Génération terminée sans URL.", "error");
      }
    } catch (e) {
      toast(`Erreur génération vidéo. ${e?.message || e}`, "error");
    } finally {
      setBusy(false);
    }
  };


  // ==== Premiere/XML export (best-effort Final Cut Pro XML for Premiere import) ====
  const downloadPremiereXML = () => {
    try {
      const xml = buildPremiereXML();
      if (!xml) return;
      const base = (project?.title || project?.name || scenarioForm?.title || "weave")
        .toString()
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_\-]/g, "");
      const fname = `${base || "weave"}_premiere.xml`;
      wvDownloadTextFile(fname, xml, "application/xml");
      toast("Premiere/XML exported (download).", "success");
    } catch (e) {
      console.error("downloadPremiereXML error", e);
      toast("Export failed.", "error");
    }
  };

  const buildPremiereXML = () => {
    const fps = Number(exportFPS ?? 30) || 30;
    const res = String(exportRes || "1920x1080");
    const [wStr, hStr] = res.split("x");
    const width = Number(wStr) || 1920;
    const height = Number(hStr) || 1080;

    const ids = (Array.isArray(selectedMediaIds) ? selectedMediaIds : []).map((x) => String(x));
    if (!ids.length) {
      toast("Select (approve) at least one thread to export.", "warning");
      return null;
    }
    const idSet = new Set(ids);
    const orderIndex = new Map(ids.map((mid, idx) => [String(mid), idx]));

    // Order: follow scenario steps, then NO SLOT
    const orderedMedia = [];
    const pushUsedFromStep = (stepKey) => {
      const arr = mediaInStep[stepKey] || [];
      const used = arr.filter((m) => idSet.has(String(m?.id ?? m?.media_id ?? m?.mediaId)));
      used.sort((a, b) => (orderIndex.get(String(a.id)) ?? 1e9) - (orderIndex.get(String(b.id)) ?? 1e9));
      orderedMedia.push(...used);
    };

    const steps = Array.isArray(scenarioForm.steps) ? scenarioForm.steps : [];
    if (steps.length) {
      steps.forEach((st) => pushUsedFromStep(String(st.id)));
      pushUsedFromStep("no-step");
    } else {
      ids.forEach((mid) => { if (mediaById[String(mid)]) orderedMedia.push(mediaById[String(mid)]); });
    }

    const uniq = new Set();
    const clips = orderedMedia.filter((m) => {
      const mid = String(m?.id ?? "");
      if (!mid || uniq.has(mid)) return false;
      uniq.add(mid);
      return true;
    });

    if (!clips.length) {
      toast("Nothing to export.", "warning");
      return null;
    }

    const timebase = fps;
    let pos = 0; // frames

    const clipItemsXml = clips.map((m, idx) => {
      const mid = String(m?.id ?? idx + 1);
      const url = mediaUrlById(mid);
      const isVid = isVideoUrl(url);

      const durSecRaw =
        m?.duration_sec ??
        m?.duration ??
        m?.length_sec ??
        m?.length ??
        (isVid ? 8 : 5);

      const durSec = Math.max(1, Number(durSecRaw) || (isVid ? 8 : 5));
      const frames = Math.max(1, Math.round(durSec * fps));

      const start = pos;
      const end = pos + frames;
      pos = end;

      const name = m?.title || m?.name || `clip_${idx + 1}`;
      const pathurl = url
        ? (String(url).startsWith("http") ? String(url) : `file://localhost${String(url).startsWith("/") ? "" : "/"}${String(url)}`)
        : `clip_${idx + 1}`;

      return `
        <clipitem id="clipitem-${idx + 1}">
          <name>${wvEscapeXml(name)}</name>
          <enabled>TRUE</enabled>
          <start>${start}</start>
          <end>${end}</end>
          <in>0</in>
          <out>${frames}</out>
          <file id="file-${idx + 1}">
            <name>${wvEscapeXml(name)}</name>
            <pathurl>${wvEscapeXml(pathurl)}</pathurl>
            <rate><timebase>${timebase}</timebase><ntsc>FALSE</ntsc></rate>
            <media>
              <video>
                <samplecharacteristics>
                  <width>${width}</width>
                  <height>${height}</height>
                  <anamorphic>FALSE</anamorphic>
                  <pixelaspectratio>square</pixelaspectratio>
                  <fielddominance>none</fielddominance>
                  <rate><timebase>${timebase}</timebase><ntsc>FALSE</ntsc></rate>
                </samplecharacteristics>
              </video>
            </media>
          </file>
        </clipitem>
      `.trim();
    }).join("\n");

    const seqName = (project?.title || project?.name || scenarioForm?.title || "Weave").toString().trim() || "Weave";
    const totalDur = pos;

    // Notes: Premiere may ask to relink media. Use your local files matching clip names.
    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="5">
  <sequence id="sequence-1">
    <name>${wvEscapeXml(seqName)}</name>
    <duration>${totalDur}</duration>
    <rate><timebase>${timebase}</timebase><ntsc>FALSE</ntsc></rate>
    <media>
      <video>
        <format>
          <samplecharacteristics>
            <width>${width}</width>
            <height>${height}</height>
            <rate><timebase>${timebase}</timebase><ntsc>FALSE</ntsc></rate>
          </samplecharacteristics>
        </format>
        <track>
${clipItemsXml.split("\n").map((l) => "          " + l).join("\n")}
        </track>
      </video>
    </media>
  </sequence>
</xmeml>
`;
  };

  // --- Slot detail ---
  const activeStep = activeStepKey && activeStepKey !== "no-step" ? stepById[String(activeStepKey)] : null;
  const activeMedia = useMemo(
    () => (activeMediaId != null ? (mediaById[String(activeMediaId)] || null) : null),
    [activeMediaId, mediaById]
  );

  const usedCount = useMemo(() => selectedMediaIds.length, [selectedMediaIds]);

  const slotFilledCount = useMemo(() => {
    const ids = new Set(selectedMediaIds.map(String));
    const steps = scenarioForm.steps || [];
    let filled = 0;
    steps.forEach((st) => {
      const k = String(st.id);
      const arr = mediaInStep[k] || [];
      if (arr.some((m) => ids.has(String(m.id)))) filled += 1;
    });
    return filled;
  }, [selectedMediaIds, scenarioForm.steps, mediaInStep]);

  const previewUrl = activeMediaId != null ? mediaUrlById(activeMediaId) : "";
  const previewIsVideo = isVideoUrl(previewUrl);

  const publicRoute = "/public";
  const studioRoute = id ? `/project/${id}/studio` : publicRoute;
  const blueprintRoute = `/wizard?edit=true&id=${id}`;
    
  const forumRoute = id ? `/projects/${id}/forum` : publicRoute;
const contributorStudioRoute = id
  ? `/project/${id}/contributor-studio`
  : publicRoute;
  const displayProjectTitle = (() => {
    const raw = (project?.title || project?.name || scenarioForm?.title || "").toString().trim();
    if (!raw) return "The weave";
    if (/^bread\s+staming$/i.test(raw)) return "The weave";
    return raw;
  })();

  const currentIdentityCandidates = useMemo(
    () => wvCurrentIdentityCandidates(token),
    [token]
  );
  const ownerIdentityCandidates = useMemo(
    () => wvOwnerIdentityCandidates(project, scenario),
    [project, scenario]
  );

  const roleReady = !!project || !!scenario;

  const isCreator = useMemo(() => {
    if (!roleReady) return false;
    if (!currentIdentityCandidates.length || !ownerIdentityCandidates.length) return false;
    const mine = new Set(currentIdentityCandidates);
    return ownerIdentityCandidates.some((value) => mine.has(value));
  }, [roleReady, currentIdentityCandidates, ownerIdentityCandidates]);

  const isWeaver = roleReady && !isCreator;

  const activeStepText = activeStep
    ? String(
        activeStep.note ||
        activeStep.description ||
        activeStep.text ||
        activeStep.prompt ||
        activeStep.caption ||
        activeStep.story ||
        activeStep.script ||
        activeStep.instructions ||
        ""
      ).trim()
    : "";

  // Montage preview (mock): pick one media per slot (prefer "Used") so the preview represents ALL slots.
  const montagePreviewSlots = useMemo(() => {
    const steps = Array.isArray(scenarioForm.steps) ? scenarioForm.steps : [];
    const usedSet = new Set((selectedMediaIds || []).map((x) => String(x)));

    const out = [];
    const seen = new Set();

    const pickFromItems = (items) => {
      if (!Array.isArray(items) || !items.length) return null;
      const used = items.find((m) => usedSet.has(String(m?.id ?? m?.media_id ?? m?.mediaId)));
      return used || items[0] || null;
    };

    // 1) One representative contribution per slot (step order)
    steps.forEach((st, idx) => {
      const k = String(st?.id ?? "");
      if (!k) return;
      const items = mediaInStep[k] || [];
      const m = pickFromItems(items);
      const mid = m?.id ?? m?.media_id ?? m?.mediaId;
      if (mid == null) return;

      const sid = String(mid);
      if (seen.has(sid)) return;
      seen.add(sid);

      out.push({
        stepKey: k,
        slotIndex: idx + 1,
        label: st?.location || st?.place || st?.title || `Slot ${idx + 1}`,
        mediaId: mid,
        url: mediaUrlById(mid),
        used: usedSet.has(sid),
      });
    });

    // 2) Fallback (no steps or empty slots): use selection order, then any project media
    if (!out.length) {
      const picked = [];
      (selectedMediaIds || []).forEach((x) => {
        const s = String(x);
        if (!seen.has(s)) { seen.add(s); picked.push(s); }
      });
      (projectMedia || []).forEach((m) => {
        const mid = m?.id ?? m?.media_id ?? m?.mediaId;
        if (mid == null) return;
        const s = String(mid);
        if (!seen.has(s)) { seen.add(s); picked.push(s); }
      });

      picked.slice(0, 12).forEach((s, i) => {
        out.push({
          stepKey: "any",
          slotIndex: i + 1,
          label: `Clip ${i + 1}`,
          mediaId: s,
          url: mediaUrlById(s),
          used: usedSet.has(String(s)),
        });
      });
    }

    return out;
  }, [scenarioForm.steps, mediaInStep, selectedMediaIds, projectMedia, mediaById]);

  const montagePreviewUrls = useMemo(
    () => (montagePreviewSlots || []).map((x) => x.url).filter(Boolean),
    [montagePreviewSlots]
  );

  const tplKeyRaw = String(selectedTemplateObj?.baseId || selectedTemplateObj?.id || exportTemplate || "classic");
  const tplKey = tplKeyRaw.startsWith("viby_") ? (selectedTemplateObj?._raw?.baseId || "classic") : tplKeyRaw;

  const onSelectTemplate = (tplId) => {
    setExportTemplate(String(tplId));
    setPreviewMode("montage");
  };

    const templateSettingsControls = (
    <>
      <div className="wv-settings-tabs mb-3" role="tablist" aria-label="Panel sections">
        <button
          type="button"
          className={"wv-settings-tabbtn " + (settingsTab === "templates" ? "is-on" : "")}
          onClick={() => setSettingsTab("templates")}
        >
          Templates
        </button>
        <button
          type="button"
          className={"wv-settings-tabbtn " + (settingsTab === "settings" ? "is-on" : "")}
          onClick={() => setSettingsTab("settings")}
        >
          Settings
        </button>
      </div>

      {settingsTab === "templates" ? (
        <>
          <label className="small mb-1" style={{ opacity: 0.8 }}>Color intensity</label>
          <input
            type="range"
            min="0"
            max="100"
            value={Number(((vibyOverrides && vibyOverrides[exportTemplate]) || vibyDraft)?.filter_intensity ?? 60)}
            onChange={(e) => {
              const v = Number(e.target.value);
              setVibyOverrides((prev) => ({
                ...(prev || {}),
                [exportTemplate]: { ...wvNormalizeViby((prev && prev[exportTemplate]) || vibyDraft), filter_intensity: v }
              }));
            }}
            className="form-range mb-3"
          />

          <div className="wv-tplHead mb-2">
            <input
              className="form-control form-control-sm wv-tplSearch"
              placeholder="Search…"
              value={tplQuery}
              onChange={(e) => setTplQuery(e.target.value)}
            />

            <button
              type="button"
              className="btn btn-sm btn-light wv-actionbtn wv-iconbtn"
              onClick={() => setTplOpen((v) => !v)}
              title={tplOpen ? "Hide template library" : "Show template library"}
              aria-label={tplOpen ? "Hide template library" : "Show template library"}
            >
              <i className={"bi " + (tplOpen ? "bi-chevron-up" : "bi-chevron-down")} />
            </button>
          </div>

          {tplOpen ? (
            <>
              <div className="wv-tplCats">
                {["ALL", ...(templateCats || [])].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={"wv-tplChip " + (tplCat === c ? "is-on" : "")}
                    onClick={() => setTplCat(c)}
                  >
                    {c === "ALL" ? "All" : c}
                    {tplCat === c ? <i className="bi bi-check2 ms-1" /> : null}
                  </button>
                ))}
              </div>

              <div className="wv-tplGrid">
                {filteredTemplates.map((t) => {
                  const active = String(exportTemplate) === String(t.id);

                  const urls = (montagePreviewUrls || []).slice(0, 4);
                  const u = (i) => urls[i] || "";

                  const Tile = ({ url, style }) => {
                    if (!url) return <div className="wv-tplTile skel" style={style} />;
                    if (isVideoUrl(url)) {
                      return (
                        <video
                          className="wv-tplTile"
                          style={style}
                          src={url}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      );
                    }
                    return <img className="wv-tplTile" style={style} src={url} alt="" loading="lazy" />;
                  };

                  const Canvas = () => {
                    const key = String(t.id || "");
                    const isFx = key.startsWith("fx_");
                    const isWall = key.startsWith("wall_");
                    const isBg = key.startsWith("bg_");
                    const isAni = key.startsWith("ani_");
                    const isViby = key.startsWith("viby_") || key === "viby";

                    if (key === "classic") {
                      return (
                        <div className="wv-tplCanvas classic">
                          <div className="wv-tplStack">
                            <div className="wv-tplLine" />
                            <div className="wv-tplLine dim" />
                            <div className="wv-tplLine dim2" />
                          </div>
                          <div className="wv-tplHint">Sequential</div>
                        </div>
                      );
                    }

                    if (key === "grid_2x2" || key.includes("2x2")) {
                      return (
                        <div className="wv-tplCanvas">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "50%", height: "50%" }} />
                          <Tile url={u(1)} style={{ left: "50%", top: 0, width: "50%", height: "50%" }} />
                          <Tile url={u(2)} style={{ left: 0, top: "50%", width: "50%", height: "50%" }} />
                          <Tile url={u(3)} style={{ left: "50%", top: "50%", width: "50%", height: "50%" }} />
                          <div className="wv-tplBadgeMini">2×2</div>
                        </div>
                      );
                    }

                    if (key === "split_v" || key.includes("split_v")) {
                      return (
                        <div className="wv-tplCanvas">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "50%", height: "100%" }} />
                          <Tile url={u(1)} style={{ left: "50%", top: 0, width: "50%", height: "100%" }} />
                          <div className="wv-tplBadgeMini">Split</div>
                        </div>
                      );
                    }

                    if (key === "split_h" || key.includes("split_h")) {
                      return (
                        <div className="wv-tplCanvas">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "100%", height: "50%" }} />
                          <Tile url={u(1)} style={{ left: 0, top: "50%", width: "100%", height: "50%" }} />
                          <div className="wv-tplBadgeMini">Split</div>
                        </div>
                      );
                    }

                    if (key === "hero_3" || key.includes("hero")) {
                      return (
                        <div className="wv-tplCanvas">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "68%", height: "100%" }} />
                          <Tile url={u(1)} style={{ left: "68%", top: 0, width: "32%", height: "33.333%" }} />
                          <Tile url={u(2)} style={{ left: "68%", top: "33.333%", width: "32%", height: "33.333%" }} />
                          <Tile url={u(3)} style={{ left: "68%", top: "66.666%", width: "32%", height: "33.333%" }} />
                          <div className="wv-tplBadgeMini">Hero</div>
                        </div>
                      );
                    }

                    if (isFx) {
                      const name = (t.label || "FX").toString();
                      return (
                        <div className="wv-tplCanvas fx">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "100%", height: "100%" }} />
                          <div className="wv-tplShade" />
                          <div className="wv-tplPill">FX</div>
                          <div className="wv-tplBottom">{name}</div>
                        </div>
                      );
                    }

                    if (isViby) {
                      const name = (t.label || "Viby").toString();
                      return (
                        <div className="wv-tplCanvas viby">
                          <div className="wv-tplVibyBg" />
                          <Tile url={u(0)} style={{ left: "14%", top: "16%", width: "72%", height: "68%", borderRadius: 14 }} />
                          <div className="wv-tplPill">VIBY</div>
                          <div className="wv-tplBottom">{name}</div>
                        </div>
                      );
                    }

                    if (isWall) {
                      return (
                        <div className="wv-tplCanvas wall">
                          <Tile url={u(0)} style={{ left: 0, top: 0, width: "100%", height: "100%" }} />
                          <div className="wv-tplShade" />
                          <div className="wv-tplPill">WALL</div>
                        </div>
                      );
                    }

                    if (isBg) {
                      return (
                        <div className="wv-tplCanvas bg">
                          <div className="wv-tplBgAnim" />
                          <Tile url={u(0)} style={{ left: 10, top: 10, width: "46%", height: "44%", borderRadius: 12 }} />
                          <Tile url={u(1)} style={{ left: "52%", top: 10, width: "38%", height: "28%", borderRadius: 12 }} />
                          <Tile url={u(2)} style={{ left: "52%", top: "44%", width: "38%", height: "46%", borderRadius: 12 }} />
                          <div className="wv-tplPill">BG</div>
                        </div>
                      );
                    }

                    if (isAni) {
                      return (
                        <div className="wv-tplCanvas anim">
                          <div className="wv-tplAnimBg" />
                          <Tile url={u(0)} style={{ left: 14, top: 26, width: "44%", height: "60%", borderRadius: 14, transform: "rotate(-2deg)" }} />
                          <Tile url={u(1)} style={{ left: "44%", top: 18, width: "44%", height: "60%", borderRadius: 14, transform: "rotate(2deg)" }} />
                          <div className="wv-tplPill">ANIM</div>
                        </div>
                      );
                    }

                    return (
                      <div className="wv-tplCanvas">
                        <Tile url={u(0)} style={{ left: 0, top: 0, width: "60%", height: "60%" }} />
                        <Tile url={u(1)} style={{ left: "60%", top: 0, width: "40%", height: "45%" }} />
                        <Tile url={u(2)} style={{ left: 0, top: "60%", width: "38%", height: "40%" }} />
                        <Tile url={u(3)} style={{ left: "38%", top: "60%", width: "62%", height: "40%" }} />
                        <div className="wv-tplBadgeMini">Weave</div>
                      </div>
                    );
                  };

                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={"wv-tplCard " + (active ? "is-active" : "")}
                      onClick={() => onSelectTemplate(t.id)}
                      title={t.description || t.label || t.id}
                    >
                      <div className="wv-tplCanvasWrap">
                        <Canvas />
                      </div>
                      <div className="wv-tplCardBody">
                        <div className="wv-tplRow">
                          <div className="wv-tplTitle text-truncate">{t.label || t.id}</div>
                          <span className="wv-tplTag">{getTplCat(t)}</span>
                        </div>
                        <div className="wv-tplSub text-truncate">
                          {t.description ? t.description : (active ? "Selected" : "Click to select")}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="small px-1 py-2" style={{ opacity: 0.72 }}>
              Template library hidden. Click the arrow to reopen it.
            </div>
          )}
        </>
      ) : (
        <>
          <label className="small mb-1" style={{ opacity: 0.8 }}>Music volume</label>
          <input
            type="range"
            min="0"
            max="100"
            value={Number(musicVolume ?? 20)}
            onChange={(e) => setMusicVolume(Number(e.target.value))}
            className="form-range mb-3"
          />

          <label className="small mb-1" style={{ opacity: 0.8 }}>Transition</label>
          <select
            className="form-select form-select-sm mb-3"
            value={transition}
            onChange={(e) => setTransition(e.target.value)}
          >
            <option value="none">None</option>
            <option value="fade">Fade</option>
            <option value="slide">Slide</option>
            <option value="zoom">Zoom</option>
            <option value="wipe">Wipe</option>
          </select>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light wv-actionbtn"
              onClick={() => { setMontageMode("IA"); toast("Mode assisté (IA)", "info"); }}
            >
              <i className="bi bi-stars me-1" /> Assisted
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light wv-actionbtn"
              onClick={() => { setTheme("Documentaire"); toast("Theme: Documentaire", "info"); }}
            >
              <i className="bi bi-palette me-1" /> Theme
            </button>
          </div>

          <div className="small mt-3" style={{ opacity: 0.75 }}>
            <div className="mb-1"><i className="bi bi-info-circle me-1" /> Settings are saved with the montage.</div>
            <div>Tip: Approve threads you want, then click <b>Save</b> before finalizing.</div>
          </div>
        </>
      )}
    </>
  );


  return (

<div
  className="wv-studio-v2"
  style={{
    "--wv-topbar-h": String(layoutVars.topbarH) + "px",
    "--wv-slotline-h": String(layoutVars.slotlineH) + "px",
    "--wv-fixed-left": String(layoutVars.fixedLeft) + "px",
    "--wv-fixed-right": String(layoutVars.fixedRight) + "px",
  }}
>
      <style>{`        :root{
          --wv-bg: #f6f7fb;
          --wv-card: rgba(255,255,255,0.92);
          --wv-border: rgba(15,23,42,0.10);
          --wv-text: #0b1220;
          --wv-muted: rgba(11,18,32,0.70);
          --wv-accent: rgba(56,104,255,1);
          --wv-accent-soft: rgba(56,104,255,0.14);
          --wv-green: rgba(16,185,129,1);
          --wv-green-soft: rgba(16,185,129,0.12);
          --wv-red: rgba(239,68,68,1);
          --wv-red-soft: rgba(239,68,68,0.12);
        }

        .wv-studio-v2{
          /* App shell offsets (left sidebar + top navbar) */
          --wv-app-top-offset: 72px;
          --wv-app-left-offset: 0px;
          --wv-shell-top: calc(var(--wv-app-top-offset, 72px) + 10px);
          padding-top: 8px;
          min-height: 100vh;
          background:
            radial-gradient(1200px 600px at 18% 0%, rgba(56,104,255,0.12), transparent 60%),
            radial-gradient(1000px 560px at 88% 10%, rgba(0,220,255,0.10), transparent 55%),
            var(--wv-bg);
          color: var(--wv-text);
        }

        .wv-studio-v2 .card{
          background: var(--wv-card);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          color: inherit;
          box-shadow: 0 10px 26px rgba(2,6,23,0.06);
        }

        .wv-studio-v2-topbar{
          top: var(--wv-shell-top);
          z-index: 3000;
          margin-left: calc(14px + var(--wv-app-left-offset, 0px));
          margin-right: 14px;
          margin-top: 70px;
          margin-bottom: 10px;
          background: rgba(255,255,255,0.88);
          backdrop-filter: blur(10px);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(2,6,23,0.06);
          padding: 10px 14px;
        }

        .wv-studio-v2-title h5{
          margin: 0;
          font-size: 0.98rem;
          letter-spacing: .2px;
        }
        .wv-studio-v2-sub{
          margin: 0;
          font-size: 0.78rem;
          color: var(--wv-muted);
        }

        /* Always 3 columns for desktop (14" & 17") */
        .wv-studio-v2-body{
          display: grid;
          grid-template-columns: clamp(300px, 26vw, 420px) minmax(560px, 1fr) clamp(280px, 22vw, 420px);
          gap: 12px;
                    padding: 14px;
          padding-left: calc(14px + var(--wv-app-left-offset, 0px));
          padding-top: 18px;
          align-items: start;
        }


        /* 2-column layout on 14" (hide right panel into drawer) */
        .wv-studio-v2-body.wv-body-2col{
          grid-template-columns: clamp(320px, 30vw, 460px) minmax(480px, 1fr);
        }
        .wv-studio-v2-body.wv-body-right-collapsed{
          grid-template-columns: clamp(300px, 26vw, 420px) minmax(760px, 1fr);
        }

        /* Settings drawer (14") */
        .wv-settings-backdrop{
          position: fixed;
          inset: 0;
          z-index: 6000;
          background: rgba(2,6,23,0.42);
          display: flex;
          justify-content: flex-end;
          align-items: stretch;
          padding: 14px;
        }
        .wv-settings-drawer{
          width: min(420px, 92vw);
          height: calc(100vh - 28px);
          overflow: auto;
          background: var(--wv-card);
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          box-shadow: 0 18px 50px rgba(2,6,23,0.28);
          padding: 14px;
        }
        .wv-settings-close{ border-radius: 12px; }

        /* 14" laptop tuning (keep 3 columns, reduce spacing + sizes) */
        @media (max-width: 1400px){
          .wv-studio-v2-body{
            grid-template-columns: 290px minmax(400px, 1fr) 330px;
            gap: 10px;
            padding: 10px;
            padding-left: calc(10px + var(--wv-app-left-offset, 0px));
          }
          .wv-studio-v2-body.wv-body-right-collapsed{
            grid-template-columns: 280px minmax(460px, 1fr);
          }
        }


@media (max-width: 1400px){
  .wv-threadGrid{ gap: 8px; }
  .wv-thumb-wrap{ height: 104px; }
  .wv-thread-title{ font-size: 0.80rem; }
  .wv-thread-meta{ font-size: 0.70rem; }
}

        /* Only stack on truly small screens */
        @media (max-width: 1100px){
          .wv-studio-v2-body{ grid-template-columns: 1fr; }
          .wv-preview{ width: 100%; }
        }

        .wv-panel{ min-height: 0; display:flex; flex-direction:column; gap:10px; }
        .wv-panel-scroll{
          overflow: auto;
          min-height: 0;
          max-height: calc(100vh - 260px);
        }

        .wv-rightCard{
          display:flex;
          flex-direction:column;
          min-height: 0;
          overflow: hidden;
        }
        .wv-rightScroll{
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          padding-right: 2px;
        }

        .wv-chip{
          font-size: 0.70rem;
          padding: 3px 10px;
          border-radius: 999px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          color: rgba(11,18,32,0.82);
          white-space: nowrap;
        }
        .wv-chip--green{
          border-color: rgba(16,185,129,0.25);
          background: var(--wv-green-soft);
          color: rgba(6,95,70,0.95);
        }
        .wv-chip--red{
          border-color: rgba(239,68,68,0.22);
          background: var(--wv-red-soft);
          color: rgba(153,27,27,0.95);
        }

        .wv-tab{
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.70);
          color: rgba(11,18,32,0.86);
          font-size: 0.78rem;
        }
        .wv-tab.active{
          background: var(--wv-accent-soft);
          border-color: rgba(56,104,255,0.30);
          color: rgba(11,18,32,0.92);
        }


        .wv-threadGrid{
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        @media (max-width: 520px){
          .wv-threadGrid{ grid-template-columns: 1fr; }
        }

        .wv-thread{
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 8px;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.78);
          cursor: pointer;
        }
        .wv-thread:hover{
          background: rgba(255,255,255,0.96);
          border-color: rgba(15,23,42,0.14);
        }
        .wv-thread.active{
          outline: 2px solid rgba(56,104,255,0.32);
          background: rgba(56,104,255,0.06);
        }

        .wv-thumb-wrap{
          position: relative;
          width: 100%;
          height: 112px;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(15,23,42,0.03);
        }
        .wv-thumb{
          width: 100%;
          height: 100%;
          object-fit: cover;
          display:block;
        }
        .wv-contrib-badge{
          cursor: pointer;
          position: absolute;
          right: 6px;
          bottom: 6px;
          z-index: 2;
          box-shadow: 0 10px 18px rgba(2,6,23,0.10);
        }

        .wv-thread-actions{
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 3;
        }
        .wv-thread-actions .btn{
          border-radius: 999px;
          padding: 4px 6px;
          background: rgba(255,255,255,0.86);
          backdrop-filter: blur(8px);
          border-color: rgba(15,23,42,0.16);
        }
        .wv-thread-actions .btn:hover{
          background: rgba(255,255,255,0.96);
        }

        .wv-thread-row{
          display:flex;
          align-items:center;
          gap: 8px;
          min-width: 0;
        }
        .wv-thread-title{ margin:0; font-size:0.82rem; line-height:1.15; color: rgba(11,18,32,0.92); flex: 1 1 auto; min-width: 0; }
        .wv-thread-meta{ margin:2px 0 0; font-size:0.70rem; color: var(--wv-muted); }
        .wv-thread-status{
          width: 8px;
          height: 8px;
          border-radius: 999px;
          flex: 0 0 auto;
          background: rgba(148,163,184,0.7);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.7);
        }
        .wv-thread-status.is-used{
          background: rgba(16,185,129,0.92);
        }


        .wv-preview-wrap{ padding: 10px; }
        .wv-preview{
          width: 100%;
          height: clamp(300px, 46vh, 620px);
          margin: 0 auto;
          display: block;
          border-radius: 18px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          object-fit: cover;
        }
        video.wv-preview{ object-fit: contain; background: rgba(15,23,42,0.03); }

        .wv-preview-top{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }
        .wv-preview-context{
          font-weight: 700;
          font-size: .92rem;
          color: rgba(11,18,32,0.90);
          min-width: 0;
        }
        .wv-preview-metaCard{
          margin-top: 12px;
          padding: 12px;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.82);
        }
        .wv-preview-metaHead{
          display:flex;
          align-items:center;
          gap: 10px;
          margin-bottom: 8px;
        }
        .wv-preview-title{
          font-weight: 700;
          font-size: .92rem;
          color: rgba(11,18,32,0.92);
        }
        .wv-preview-sub{
          font-size: .76rem;
          color: var(--wv-muted);
        }
        .wv-preview-desc{
          font-size: .84rem;
          line-height: 1.45;
          color: rgba(11,18,32,0.82);
          white-space: pre-wrap;
        }
        .wv-slot-note{
          margin-top: 12px;
          padding: 12px;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          font-size: .84rem;
          color: rgba(11,18,32,0.82);
        }
        .wv-slot-note-label{
          font-size: .72rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .04em;
          color: rgba(11,18,32,0.58);
          margin-bottom: 4px;
        }
        .wv-preview-modes{
          display:inline-flex;
          border: 1px solid rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.72);
          border-radius: 999px;
          padding: 2px;
          gap: 2px;
        }
        .wv-modebtn{
          border: 0;
          border-radius: 999px;
          padding: 6px 10px;
          font-weight: 900;
          font-size: .76rem;
          background: transparent;
          color: rgba(11,18,32,0.74);
          display:inline-flex;
          align-items:center;
          gap: 6px;
        }
        .wv-modebtn.is-on{
          background: rgba(56,104,255,0.14);
          color: rgba(11,18,32,0.92);
        }

        .wv-montageMock{
          width: 100%;
          height: clamp(240px, 38vh, 460px);
          border-radius: 18px;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
          overflow: hidden;
          position: relative;
        }
        .wv-montageBadge{
          position:absolute;
          left: 12px;
          top: 12px;
          z-index: 3;
          font-size: .72rem;
          font-weight: 900;
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(11,18,32,0.62);
          color: rgba(255,255,255,0.92);
          border: 1px solid rgba(255,255,255,0.22);
        }
        .wv-montageHint{
          position:absolute;
          right: 12px;
          top: 12px;
          z-index: 3;
          font-size: .72rem;
          font-weight: 900;
          padding: 4px 10px;
          border-radius: 999px;
          background: rgba(255,255,255,0.74);
          color: rgba(11,18,32,0.78);
          border: 1px solid rgba(15,23,42,0.12);
        }
        .wv-montageTile{
          width: 100%;
          height: 100%;
          object-fit: cover;
          display:block;
        }
        .wv-montageCell{
          border: 0;
          padding: 0;
          background: transparent;
          border-radius: 14px;
          overflow: hidden;
          cursor: zoom-in;
          display: block;
          width: 100%;
          height: 100%;
        }
        .wv-montageCell:focus{ outline: 2px solid rgba(56,104,255,0.35); outline-offset: 2px; }

        .wv-montageFilmstrip{
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          padding: 46px 12px 12px;
          display: flex;
          gap: 10px;
          overflow-x: auto;
          align-items: stretch;
          -webkit-overflow-scrolling: touch;
        }
        .wv-montageFilmstrip::-webkit-scrollbar{ height: 8px; }
        .wv-montageFilmstrip::-webkit-scrollbar-thumb{ background: rgba(15,23,42,0.16); border-radius: 999px; }

        .wv-filmItem{
          flex: 0 0 auto;
          width: clamp(160px, 24vw, 260px);
          height: 100%;
          border-radius: 14px;
          position: relative;
          border: 1px solid rgba(255,255,255,0.28);
          background: rgba(255,255,255,0.10);
        }
        .wv-filmTag{
          position: absolute;
          left: 10px;
          bottom: 10px;
          z-index: 3;
          font-size: .70rem;
          font-weight: 900;
          padding: 3px 8px;
          border-radius: 999px;
          background: rgba(11,18,32,0.62);
          color: rgba(255,255,255,0.92);
          border: 1px solid rgba(255,255,255,0.22);
        }
        .wv-filmTag.is-used{
          background: rgba(16,185,129,0.75);
          border-color: rgba(16,185,129,0.35);
        }


        .wv-montageGrid{
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          display: grid;
          gap: 4px;
          padding: 4px;
        }
        .wv-montageGrid.grid2x2{ grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
        .wv-montageGrid.splitV{ grid-template-columns: 1fr 1fr; }
        .wv-montageGrid.splitH{ grid-template-rows: 1fr 1fr; }
        .wv-montageGrid.strips3{ grid-template-columns: 1fr 1fr 1fr; }
        .wv-montageGrid.single{ grid-template-columns: 1fr; grid-template-rows: 1fr; padding: 0; gap: 0; }

        .wv-montageHero3{
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: 1.35fr 1fr;
          gap: 4px;
          padding: 4px;
        }
        .wv-montageHero3 .hero{ border-radius: 14px; overflow:hidden; }
        .wv-montageHero3 .stack{ display:grid; grid-template-rows: 1fr 1fr 1fr; gap: 4px; }
        .wv-montageHero3 .stack > *{ border-radius: 14px; overflow:hidden; }

        .wv-montageBg{
          position:absolute;
          inset:0;
          filter: blur(18px);
          transform: scale(1.12);
          opacity: .45;
          object-fit: cover;
          width:100%;
          height:100%;
        }

        .wv-montageCorners{
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          padding: 10px;
          display: grid;
          place-items: stretch;
        }
        .wv-montageCorners .cgrid{
          width:100%;
          height:100%;
          display:grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 10px;
        }
        .wv-montageCorners .cgrid > *{
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.28);
          background: rgba(255,255,255,0.12);
        }


        .wv-settings-tabs{
          display:inline-flex;
          gap: 6px;
          padding: 4px;
          border-radius: 999px;
          border: 1px solid rgba(15,23,42,0.10);
          background: rgba(15,23,42,0.04);
        }
        .wv-settings-tabbtn{
          border: 0;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: .76rem;
          font-weight: 800;
          background: transparent;
          color: rgba(11,18,32,0.68);
        }
        .wv-settings-tabbtn.is-on{
          background: rgba(56,104,255,0.14);
          color: rgba(11,18,32,0.92);
        }

        /* --- Templates (JourneyAdmin-like previews) --- */
        .wv-tplHead{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
        .wv-tplSearch{ flex: 1 1 auto; min-width: 0; border-radius: 999px !important; }
        .wv-tplToggle{ border-radius: 999px !important; font-weight: 700; }
        .wv-tplCats{
          display:flex;
          gap: 8px;
          overflow-x:auto;
          padding: 6px 2px 10px;
          -webkit-overflow-scrolling: touch;
        }
        .wv-tplCats::-webkit-scrollbar{ height: 6px; }
        .wv-tplCats::-webkit-scrollbar-thumb{ background: rgba(15,23,42,0.14); border-radius: 999px; }
        .wv-tplChip{
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.72);
          color: rgba(11,18,32,0.86);
          border-radius: 999px;
          padding: .35rem .65rem;
          font-weight: 700;
          font-size: .74rem;
          white-space: nowrap;
          transition: background .12s ease, border-color .12s ease, transform .12s ease;
        }
        .wv-tplChip:hover{ transform: translateY(-1px); background: rgba(255,255,255,0.95); }
        .wv-tplChip.is-on{
          background: rgba(56,104,255,0.12);
          border-color: rgba(56,104,255,0.28);
          color: rgba(11,18,32,0.92);
        }

        .wv-tplGrid{
          display:grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .wv-tplCard{
          text-align:left;
          border: 1px solid var(--wv-border);
          border-radius: 18px;
          background: rgba(255,255,255,0.70);
          overflow:hidden;
          padding: 0;
          cursor: pointer;
          box-shadow: 0 10px 22px rgba(2,6,23,0.06);
        }
        .wv-tplCard:hover{ background: rgba(255,255,255,0.95); border-color: rgba(15,23,42,0.14); }
        .wv-tplCard.is-active{
          outline: 2px solid rgba(56,104,255,0.32);
          background: rgba(56,104,255,0.06);
        }
        .wv-tplCanvasWrap{
          height: 118px;
          background: rgba(15,23,42,0.04);
          border-bottom: 1px solid var(--wv-border);
          position: relative;
        }
        .wv-tplCanvas{
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .wv-tplTile{
          position: absolute;
          width: 50%;
          height: 50%;
          object-fit: cover;
          background: rgba(15,23,42,0.06);
        }
        .wv-tplTile.skel{
          background:
            linear-gradient(90deg, rgba(15,23,42,0.06), rgba(15,23,42,0.03), rgba(15,23,42,0.06));
          background-size: 200% 100%;
          animation: wv-skel 1.2s infinite;
        }
        @keyframes wv-skel{
          0%{ background-position: 200% 0; }
          100%{ background-position: -200% 0; }
        }
        .wv-tplCardBody{ padding: 10px 10px 12px; }
        .wv-tplRow{ display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .wv-tplTitle{ font-weight: 800; font-size: .86rem; color: rgba(11,18,32,0.92); min-width:0; }
        .wv-tplSub{ font-size: .74rem; color: rgba(11,18,32,0.66); margin-top: 2px; }
        .wv-tplTag{
          font-size: .68rem;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 999px;
          border: 1px solid rgba(15,23,42,0.12);
          background: rgba(255,255,255,0.78);
          color: rgba(11,18,32,0.78);
          white-space: nowrap;
        }

        .wv-tplBadgeMini{
          position:absolute;
          left: 10px;
          top: 10px;
          z-index: 2;
          font-size: .68rem;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(11,18,32,0.58);
          color: rgba(255,255,255,0.92);
          border: 1px solid rgba(255,255,255,0.22);
        }
        .wv-tplShade{
          position:absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(11,18,32,0.08), rgba(11,18,32,0.55));
          z-index: 1;
        }
        .wv-tplPill{
          position:absolute;
          left: 10px;
          top: 10px;
          z-index: 2;
          font-size: .68rem;
          font-weight: 900;
          padding: 2px 8px;
          border-radius: 999px;
          background: rgba(11,18,32,0.66);
          color: rgba(255,255,255,0.92);
          border: 1px solid rgba(255,255,255,0.22);
        }
        .wv-tplBottom{
          position:absolute;
          left: 10px;
          right: 10px;
          bottom: 10px;
          z-index: 2;
          font-weight: 900;
          font-size: .78rem;
          color: rgba(255,255,255,0.92);
          text-shadow: 0 8px 18px rgba(0,0,0,0.35);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .wv-tplStack{ position:absolute; left: 14px; right: 14px; top: 24px; }
        .wv-tplLine{
          height: 10px;
          border-radius: 999px;
          background: rgba(11,18,32,0.22);
          margin-bottom: 10px;
        }
        .wv-tplLine.dim{ opacity: .65; }
        .wv-tplLine.dim2{ opacity: .45; }
        .wv-tplHint{
          position:absolute;
          left: 14px;
          bottom: 12px;
          font-size: .74rem;
          font-weight: 900;
          color: rgba(11,18,32,0.70);
        }
        .wv-tplVibyBg{
          position:absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(56,104,255,0.18), rgba(0,220,255,0.10), rgba(255,255,255,0.20));
        }
        .wv-tplBgAnim{
          position:absolute;
          inset: -20%;
          background: radial-gradient(circle at 20% 20%, rgba(56,104,255,0.20), transparent 55%),
                      radial-gradient(circle at 80% 10%, rgba(0,220,255,0.16), transparent 58%),
                      radial-gradient(circle at 40% 90%, rgba(16,185,129,0.12), transparent 55%);
          filter: blur(12px);
          opacity: .9;
        }
        .wv-tplAnimBg{
          position:absolute;
          inset: 0;
          background: radial-gradient(circle at 30% 20%, rgba(56,104,255,0.18), transparent 60%),
                      radial-gradient(circle at 70% 80%, rgba(239,68,68,0.10), transparent 55%);
        }

        @media (max-width: 1400px){
          .wv-tplGrid{ grid-template-columns: 1fr; }
          .wv-tplCanvasWrap{ height: 128px; }
        }

        .wv-preview-clickwrap{ position: relative; }
        .wv-preview{ cursor: zoom-in; }
        .wv-zoom-trigger{
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 5;
          width: 38px;
          height: 38px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.55);
          background: rgba(11,18,32,0.55);
          color: rgba(255,255,255,0.96);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 14px 30px rgba(2,6,23,0.22);
        }
        .wv-zoom-trigger:hover{ background: rgba(11,18,32,0.72); }

        .wv-zoom-overlay{
          position: fixed;
          inset: 0;
          z-index: 7000;
          background: rgba(11,18,32,0.55);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }
        .wv-zoom-dialog{
          position: relative;
          max-width: 92vw;
          max-height: 86vh;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.22);
          background: rgba(255,255,255,0.90);
          box-shadow: 0 28px 80px rgba(2,6,23,0.35);
        }
        .wv-zoom-media{
          display: block;
          max-width: 92vw;
          max-height: 86vh;
          width: auto;
          height: auto;
          object-fit: contain;
          background: rgba(15,23,42,0.06);
        }
        .wv-zoom-close{
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 2;
          width: 40px;
          height: 40px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.55);
          background: rgba(11,18,32,0.55);
          color: rgba(255,255,255,0.96);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .wv-zoom-close:hover{ background: rgba(11,18,32,0.72); }

        .wv-slot-carousel{
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 6px;
        }
        .wv-media-pill{
          flex: 0 0 180px;
          max-width: 180px;
          cursor: pointer;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.80);
          padding: 8px;
        }
        .wv-media-pill.active{ outline: 2px solid rgba(16,185,129,0.26); }
        .wv-pill-thumb{
          width: 100%;
          height: 86px;
          border-radius: 12px;
          object-fit: cover;
          border: 1px solid var(--wv-border);
          background: rgba(15,23,42,0.03);
        }

        .wv-slotline{ padding: 12px; }
        .wv-slotline-row{
          display: flex;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 8px;
        }
        .wv-slot{
          flex: 0 0 190px;
          max-width: 190px;
          border-radius: 18px;
          border: 1px solid var(--wv-border);
          background: rgba(255,255,255,0.80);
          padding: 10px;
          cursor: pointer;
        }
        .wv-slot:hover{ background: rgba(255,255,255,0.96); }
        .wv-slot.active{
          outline: 2px solid rgba(56,104,255,0.32);
          background: rgba(56,104,255,0.06);
        }
        .wv-slot.dropover{ outline: 2px dashed rgba(16,185,129,0.35); }

        .wv-slot-title{ margin:0; font-size:0.78rem; color: rgba(11,18,32,0.86); }
        .wv-slot-sub{ margin:0; font-size:0.72rem; color: var(--wv-muted); }

        .wv-slot-note{
          margin-top: 12px;
          padding: 12px 14px;
          border-radius: 16px;
          border: 1px solid var(--wv-border);
          background: rgba(56,104,255,0.05);
        }
        .wv-slot-note-title{
          margin: 0 0 6px;
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: rgba(11,18,32,0.72);
        }
        .wv-slot-note-text{
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.45;
          color: rgba(11,18,32,0.92);
          white-space: pre-wrap;
        }

        .wv-mini{
          width: 100%;
          height: 92px;
          border-radius: 14px;
          object-fit: cover;
          border: 1px solid var(--wv-border);
          margin-top: 8px;
          background: rgba(15,23,42,0.03);
        }

        /* Make form controls match Weavevo (light) */
        .wv-studio-v2 .form-control,
        .wv-studio-v2 .form-select{
          border-radius: 14px;
          border-color: rgba(15,23,42,0.14);
          background: rgba(255,255,255,0.90);
          color: rgba(11,18,32,0.92);
        }
        .wv-studio-v2 .form-control::placeholder{ color: rgba(11,18,32,0.50); }


/* --- Studio V2: action bar + 14" fit --- */
.wv-actionbar{
  display:flex;
  align-items:center;
  gap: 10px;
  padding: 8px;
  border-radius: 18px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(15,23,42,0.04);
}
.wv-actionbtn{
  border-radius: 14px !important;
  font-weight: 600;
  letter-spacing: .2px;
}
.wv-topbar-note{
  margin-top: 8px;
  font-size: 0.78rem;
  color: rgba(11,18,32,0.72);
}

/* Slot line pinned for 14" laptops so you always see slots */
.wv-slotline-fixed{
  position: fixed;
  left: var(--wv-fixed-left, calc(14px + var(--wv-app-left-offset, 0px)));
  right: var(--wv-fixed-right, 14px);
  bottom: 12px;
  z-index: 3000;
  padding: 12px;
  background: rgba(246,247,251,0.72);
  backdrop-filter: blur(10px);
  border-top: 1px solid var(--wv-border);
}
.wv-slotline-fixed .card{
  box-shadow: 0 -10px 26px rgba(2,6,23,0.06);
}
.wv-slotline.is-collapsed .wv-slotline-row{
  display:none;
}

/* Make the editor fit above Slot Line on small screens (14") */
@media (max-width: 1450px) and (max-height: 900px){
  /* 14" laptop fit: keep everything visible (topbar + 3 cols + fixed slot line) */
  .wv-studio-v2{ overflow: hidden; }

  .wv-studio-v2-body{
    height: calc(100vh - var(--wv-shell-top) - var(--wv-topbar-h, 64px) - var(--wv-slotline-h, 220px) - 18px);
    overflow: hidden;
    min-height: 0;
  }

  .wv-panel{ height: 100%; min-height: 0; }

  .wv-left .card,
  .wv-right .card{
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .wv-panel-scroll{
    flex: 1 1 auto;
    max-height: none;
    overflow: auto;
  }

  .wv-center{
    height: 100%;
    overflow: auto;
    min-height: 0;
  }

  .wv-preview, .wv-montageMock{ height: clamp(250px, 34vh, 420px); }
  .wv-preview-wrap{ padding: 8px; }
  .wv-slotline .card{ border-radius: 16px; }
  /* Slot Line: smaller + proportional on 14" while keeping the same outer padding as large screens */
  .wv-slotline{ padding: 12px; }
  .wv-slotline-row{ gap: 8px; padding-bottom: 6px; }
  .wv-slot{ flex: 0 0 clamp(150px, 14vw, 172px); max-width: clamp(150px, 14vw, 172px); padding: 8px; border-radius: 16px; }
  .wv-slot-title{ font-size: 0.72rem; }
  .wv-slot-sub{ font-size: 0.66rem; }
  .wv-mini{ height: 72px; border-radius: 12px; margin-top: 6px; }

  /* Center: avoid nested horizontal scrollbars on 14" */
  .wv-slot-carousel{
    overflow-x: hidden;
    flex-wrap: wrap;
    padding-bottom: 0;
  }
  .wv-media-pill{
    flex: 1 1 calc(50% - 10px);
    max-width: calc(50% - 10px);
  }
}
/* Dark mode removed: Studio V2 is light-only */

/* Small icon button in topbar */
.wv-iconbtn{
  width: 36px !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  display:flex !important;
  align-items:center !important;
  justify-content:center !important;
}

`}</style>


{/* Top Bar */}
<div ref={topbarRef} className="wv-studio-v2-topbar">
  <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
    <div className="wv-studio-v2-title" style={{ minWidth: 220 }}>
      <div className="d-flex flex-column">
        <h5 className="text-truncate" style={{ maxWidth: 520 }}>
          <span className="d-inline-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <i className="bi bi-layers-half" style={{ opacity: 0.85 }} />
            <span style={{ opacity: 0.75, whiteSpace: "nowrap" }}>The weave:</span>
            <span className="text-truncate" style={{ minWidth: 0 }}>
              {displayProjectTitle}
            </span>
          </span>
        </h5>
        <p className="wv-studio-v2-sub">
          {slotFilledCount}/{(scenarioForm.steps || []).length || 0} slots filled · {usedCount} clips used
        </p>
      </div>
    </div>

    <div className="wv-actionbar ms-auto">

      {isWeaver ? (
        <a
          className="btn btn-sm btn-primary wv-actionbtn"
          href={contributorStudioRoute}
          title="Contribute to the weave"
        >
          <i className="bi bi-plus-circle me-1" /> Contribute to the weave
        </a>
      ) : null}

      {isCreator ? (
        <Link className="btn btn-sm btn-outline-secondary wv-actionbtn" to={blueprintRoute} title="Open blueprint">
          <i className="bi bi-diagram-3 me-1" /> Blueprint
        </Link>
      ) : null}

      <Link className="btn btn-sm btn-outline-secondary wv-actionbtn" to={forumRoute} title="Open forum">
        <i className="bi bi-chat-left-text me-1" /> Forum
      </Link>

      {isCreator ? (
        <Link className="btn btn-sm btn-outline-secondary wv-actionbtn" to={studioRoute} title="Open the loom">
          <i className="bi bi-grid-1x2 me-1" /> Open the loom
        </Link>
      ) : null}

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary wv-actionbtn wv-iconbtn"
        onClick={downloadPremiereXML}
        title="Export Premiere/XML (Final Cut Pro XML)"
        aria-label="Export Premiere/XML"
      >
        <i className="bi bi-download" />
      </button>

      {isSmall14 ? (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary wv-actionbtn wv-iconbtn"
          onClick={() => setSettingsOpen(true)}
          title="Template settings"
          aria-label="Template settings"
        >
          <i className="bi bi-sliders" />
        </button>
      ) : null}

      <button
        type="button"
        className="btn btn-sm btn-primary wv-actionbtn"
        onClick={generateFinalVideo}
        disabled={busy}
        title="Generate the final weave"
      >
        {busy ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-magic me-1" />}
        Finalize The Weave
      </button></div>
  </div>

 
</div>

      {/* Main Body */}
      <div className={`wv-studio-v2-body ${isSmall14 ? "wv-body-2col" : ""} ${!isSmall14 && settingsCollapsed ? "wv-body-right-collapsed" : ""}`}>
        {/* LEFT: Thread Bank */}
        <div className="wv-panel wv-left">
          <div className="card p-3">
            <div className="d-flex align-items-start justify-content-between mb-2 gap-2">
              <div style={{ minWidth: 0 }}>
                <div className="fw-semibold">The Thread Bank</div>
                <div className="small text-truncate" style={{ opacity: 0.66 }}>
                  {activeStep
                    ? (activeStep.location || activeStep.place || activeStep.title || "Current slot")
                    : (activeStepKey === "no-step" ? "No slot selected" : "Current slot")}
                </div>
              </div>
              <span className="wv-chip">{bankItems.length}</span>
            </div>

            <div className="d-flex gap-2 mb-2">
              <button className={`wv-tab ${tab === "all" ? "active" : ""}`} onClick={() => setTab("all")}>All</button>
              <button className={`wv-tab ${tab === "approved" ? "active" : ""}`} onClick={() => setTab("approved")}>Approved</button>
              <button className={`wv-tab ${tab === "rejected" ? "active" : ""}`} onClick={() => setTab("rejected")}>Rejected</button>
            </div>

            <input
              className="form-control form-control-sm"
              placeholder="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <div className="wv-panel-scroll mt-3">
              <div className="wv-threadGrid">
                {bankItems.map((m) => {
                  const mid = m.id ?? m.media_id;
                  const used = isSelected(mid);
                  const url = mediaUrlById(mid);
                  const stepK = (m.step_id == null) ? "no-step" : String(m.step_id);
                  const owner = String(m.contributor_username || m.username || m.created_by || "").replace(/^@/, "");

                  return (
                    <div
                      key={mid}
                      className={`wv-thread ${String(activeMediaId) === String(mid) ? "active" : ""}`}
                      draggable
                      onDragStart={(e) => onThreadDragStart(e, m)}
                      onClick={() => { setActiveMediaId(mid); setActiveStepKey(stepK); setPreviewMode("contribution"); }}
                      title="Drag to a Slot below"
                    >
                      <div className="wv-thumb-wrap">
                        <WVMediaThumb className="wv-thumb" src={url} placeholder="https://placehold.co/208x156?text=+" />
                        <WVAvatar m={m} usersByUsername={usersByUsername} size={32} className="wv-contrib-badge" />
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div className="wv-thread-row">
                          <p className="wv-thread-title text-truncate">{m.title || m.name || "Thread"}</p>
                          <span
                            className={"wv-thread-status " + (used ? "is-used" : "")}
                            title={used ? "Used in the weave" : "Not used yet"}
                            aria-hidden="true"
                          />
                        </div>
                        <p className="wv-thread-meta text-truncate">
                          {owner ? `@${owner}` : ""}
                          {m.location ? (owner ? ` · ${m.location}` : m.location) : ""}
                        </p>
                      </div>

                      <div className="wv-thread-actions d-flex flex-column align-items-end justify-content-center">
                        <button
                          className={`btn btn-sm ${used ? "btn-outline-secondary" : "btn-outline-success"} wv-btn`}
                          onClick={(e) => { e.stopPropagation(); toggleUse(mid); }}
                          title={used ? "Remove from weave" : "Use in weave"}
                        >
                          {used ? <i className="bi bi-x-lg" /> : <i className="bi bi-check-lg" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

                {/* CENTER: Preview */}
        <div className="wv-panel wv-center">
          <div ref={previewCardRef} className="card wv-preview-wrap">
            <div className="wv-preview-top">
              <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
                {activeStep ? (
                  <div className="wv-preview-context text-truncate">
                    {activeStep.location || activeStep.place || activeStep.title || "Current slot"}
                  </div>
                ) : activeMedia ? (
                  <div className="wv-preview-context text-truncate">
                    {activeMedia.title || activeMedia.name || "Current contribution"}
                  </div>
                ) : null}
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="wv-preview-modes" role="tablist" aria-label="Preview modes">
                  <button
                    type="button"
                    className={"wv-modebtn " + (previewMode === "contribution" ? "is-on" : "")}
                    onClick={() => setPreviewMode("contribution")}
                    title="Preview a single contribution"
                    aria-label="Contribution preview"
                  >
                    <i className="bi bi-image" />
                    Contribution
                  </button>
                  <button
                    type="button"
                    className={"wv-modebtn " + (previewMode === "montage" ? "is-on" : "")}
                    onClick={() => setPreviewMode("montage")}
                    title="Preview the montage based on the selected template"
                    aria-label="Montage preview"
                  >
                    <i className="bi bi-film" />
                    Montage
                  </button>
                </div>

                {!isSmall14 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-light wv-actionbtn"
                    onClick={() => setSettingsCollapsed((v) => !v)}
                    title={settingsCollapsed ? "Show settings" : "Hide settings"}
                    aria-label={settingsCollapsed ? "Show settings" : "Hide settings"}
                  >
                    <i className={"bi " + (settingsCollapsed ? "bi-layout-sidebar-inset" : "bi-layout-sidebar-inset-reverse")} />
                  </button>
                ) : null}

                <button
                  className="btn btn-sm btn-light wv-actionbtn"
                  onClick={() => saveSelectionAndMontage()}
                  disabled={busy}
                  title="Save selection + montage settings"
                >
                  <i className="bi bi-cloud-check me-1" /> Save
                </button>
                {finalVideoUrl ? (
                  <a className="btn btn-sm btn-outline-success wv-btn" href={finalVideoUrl} target="_blank" rel="noreferrer">
                    <i className="bi bi-play-circle me-1" /> Open video
                  </a>
                ) : null}
              </div>
            </div>

            {previewMode === "contribution" ? (
              previewUrl ? (
                <>
                  <div className="wv-preview-clickwrap">
                    {previewIsVideo ? (
                      <video
                        className="wv-preview"
                        src={previewUrl}
                        controls
                        playsInline
                        onDoubleClick={() => openZoom(previewUrl)}
                      />
                    ) : (
                      <img
                        className="wv-preview"
                        src={previewUrl}
                        alt=""
                        onClick={() => openZoom(previewUrl)}
                      />
                    )}

                    <button
                      type="button"
                      className="wv-zoom-trigger"
                      onClick={() => openZoom(previewUrl)}
                      title="View full size"
                      aria-label="View full size"
                    >
                      <i className="bi bi-arrows-fullscreen" />
                    </button>
                  </div>

                  {activeMedia ? (
                    <div className="wv-preview-metaCard">
                      <div className="wv-preview-metaHead">
                        <WVAvatar m={activeMedia} usersByUsername={usersByUsername} size={44} />
                        <div style={{ minWidth: 0 }}>
                          <div className="wv-preview-title text-truncate">
                            {activeMedia.title || activeMedia.name || "Contribution"}
                          </div>
                          <div className="wv-preview-sub text-truncate">
                            {(() => {
                              const owner = String(activeMedia.contributor_username || activeMedia.username || activeMedia.created_by || "").replace(/^@/, "");
                              const where = activeMedia.location || activeMedia.place || "";
                              if (owner && where) return `@${owner} · ${where}`;
                              if (owner) return `@${owner}`;
                              return where || "Contributor";
                            })()}
                          </div>
                        </div>
                      </div>

                      <div className="wv-preview-desc">
                        {activeMedia.description ||
                          activeMedia.caption ||
                          activeMedia.note ||
                          activeMedia.text ||
                          activeMedia.prompt ||
                          "No description provided for this contribution."}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-center d-flex align-items-center justify-content-center" style={{ height: "clamp(300px, 46vh, 620px)", opacity: 0.7 }}>
                  Select a thread to preview.
                </div>
              )
            ) : (
              finalVideoUrl ? (
                <div className="wv-preview-clickwrap">
                  <video
                    className="wv-preview"
                    src={finalVideoUrl}
                    controls
                    playsInline
                    onDoubleClick={() => openZoom(finalVideoUrl)}
                  />
                  <button
                    type="button"
                    className="wv-zoom-trigger"
                    onClick={() => openZoom(finalVideoUrl)}
                    title="View full size"
                    aria-label="View full size"
                  >
                    <i className="bi bi-arrows-fullscreen" />
                  </button>
                </div>
              ) : montagePreviewUrls.length ? (
                <div className="wv-montageMock">
                  {montagePreviewUrls[0] ? (
                    <img className="wv-montageBg" src={montagePreviewUrls[0]} alt="" />
                  ) : null}
                  <div className="wv-montageBadge">{selectedTemplateObj?.label || selectedTemplateObj?.id || exportTemplate || "Template"}</div>
                  <div className="wv-montageHint">Mock preview</div>

                  {tplKey === "corners_float" ? (
                    <div className="wv-montageCorners">
                      <div className="cgrid">
                        {[0, 1, 2, 3].map((i) => {
                          const u = montagePreviewUrls[i] || montagePreviewUrls[0] || "";
                          return (
                            <button key={i} type="button" className="wv-montageCell" onClick={() => u && openZoom(u)} title="View">
                              <WVMediaThumb className="wv-montageTile" src={u} placeholder="https://placehold.co/240x140?text=+" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : tplKey === "hero_3" ? (
                    <div className="wv-montageHero3">
                      <button type="button" className="wv-montageCell hero" onClick={() => montagePreviewUrls[0] && openZoom(montagePreviewUrls[0])} title="View">
                        <WVMediaThumb className="wv-montageTile" src={montagePreviewUrls[0]} placeholder="https://placehold.co/400x240?text=+" />
                      </button>
                      <div className="stack">
                        {[1, 2, 3].map((i) => {
                          const u = montagePreviewUrls[i] || montagePreviewUrls[0] || "";
                          return (
                            <button key={i} type="button" className="wv-montageCell" onClick={() => u && openZoom(u)} title="View">
                              <WVMediaThumb className="wv-montageTile" src={u} placeholder="https://placehold.co/240x140?text=+" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : tplKey === "classic" ? (
                    <div className="wv-montageFilmstrip">
                      {(montagePreviewSlots || []).map((it, i) => {
                        const u = it?.url || "";
                        return (
                          <button
                            key={`${it.stepKey}-${it.mediaId}-${i}`}
                            type="button"
                            className="wv-montageCell wv-filmItem"
                            onClick={() => u && openZoom(u)}
                            title={`Slot ${it.slotIndex}`}
                          >
                            <WVMediaThumb className="wv-montageTile" src={u} placeholder="https://placehold.co/400x240?text=+" />
                            <div className={"wv-filmTag " + (it?.used ? "is-used" : "")}>S{it.slotIndex}</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className={
                        "wv-montageGrid " +
                        (tplKey === "grid_2x2"
                          ? "grid2x2"
                          : tplKey === "split_v"
                            ? "splitV"
                            : tplKey === "split_h"
                              ? "splitH"
                              : tplKey === "strips_3"
                                ? "strips3"
                                : "single")
                      }
                    >
                      {(tplKey === "grid_2x2"
                        ? [0, 1, 2, 3]
                        : tplKey === "strips_3"
                          ? [0, 1, 2]
                          : (tplKey === "split_v" || tplKey === "split_h")
                            ? [0, 1]
                            : [0]
                      ).map((i) => {
                        const u = montagePreviewUrls[i] || montagePreviewUrls[0] || "";
                        return (
                          <button key={i} type="button" className="wv-montageCell" onClick={() => u && openZoom(u)} title="View">
                            <WVMediaThumb className="wv-montageTile" src={u} placeholder="https://placehold.co/400x240?text=+" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center d-flex align-items-center justify-content-center" style={{ height: "clamp(240px, 38vh, 460px)", opacity: 0.7 }}>
                  Choose a template and select at least one thread to preview the montage.
                </div>
              )
            )}

          </div>
        </div>

        {/* RIGHT: Template Settings */}
        {!isSmall14 && !settingsCollapsed ? (
          <div className="wv-panel wv-right">
            <div className="card p-3 wv-rightCard" style={syncedPreviewH ? { height: syncedPreviewH } : undefined}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold">Creative panel</div>
                <button
                  type="button"
                  className="btn btn-sm btn-light wv-settings-close"
                  onClick={() => setSettingsCollapsed(true)}
                  aria-label="Collapse settings"
                  title="Collapse settings"
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>

              <div className="wv-rightScroll">{templateSettingsControls}</div>
            </div>
          </div>
        ) : null}

      </div>

      {/* Settings Drawer (14") */}
      {isSmall14 && settingsOpen ? (
        <div className="wv-settings-backdrop" onClick={() => setSettingsOpen(false)}>
          <div className="wv-settings-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="fw-semibold">Creative panel</div>
              <button
                type="button"
                className="btn btn-sm btn-light wv-settings-close"
                onClick={() => setSettingsOpen(false)}
                aria-label="Close"
                title="Close"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="wv-panel-scroll" style={{ paddingRight: 2 }}>{templateSettingsControls}</div>
          </div>
        </div>
      ) : null}

      {/* Slot Line */}
      <div ref={slotlineRef} className={`wv-slotline ${isSmall14 ? "wv-slotline-fixed" : ""} ${slotlineCollapsed ? "is-collapsed" : ""}`}>
        <div className="card p-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="fw-semibold">The Slot Line</div>
            <div className="d-flex align-items-center gap-2">
              <span className="wv-chip">{slotFilledCount}/{(scenarioForm.steps || []).length || 0} filled</span>
              {isSmall14 ? (
                <button
                  type="button"
                  className="btn btn-sm btn-light wv-actionbtn"
                  onClick={() => setSlotlineCollapsed((v) => !v)}
                  title={slotlineCollapsed ? "Expand slot line" : "Collapse slot line"}
                >
                  <i className={`bi ${slotlineCollapsed ? "bi-chevron-up" : "bi-chevron-down"}`} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="wv-slotline-row">
            {(scenarioForm.steps || []).map((st, idx) => {
              const k = String(st.id);
              const items = mediaInStep[k] || [];
              const ids = new Set(selectedMediaIds.map(String));
              const usedInSlot = items.filter((m) => ids.has(String(m.id)));
              const previewMid = (usedInSlot[0]?.id ?? items[0]?.id) ?? null;
              const preview = previewMid ? mediaUrlById(previewMid) : "";
              const isActive = String(activeStepKey) === k;
              const isOver = dragOverStepKey === k;

              return (
                <div
                  key={k}
                  className={`wv-slot ${isActive ? "active" : ""} ${isOver ? "dropover" : ""}`}
                  onClick={() => setActiveStepKey(k)}
                  onDragOver={onDragOverStep(k)}
                  onDrop={onDropToStep(k)}
                  title="Drop a thread here"
                >
                  <p className="wv-slot-title">SLOT {idx + 1}</p>
                  <p className="wv-slot-sub text-truncate">{st.location || st.place || st.title || "—"}</p>
                  {preview ? <WVMediaThumb className="wv-mini" src={preview} placeholder="https://placehold.co/380x184?text=+" /> : <div className="wv-mini d-flex align-items-center justify-content-center" style={{ opacity: 0.6 }}>Drag & Drop</div>}
                  <div className="d-flex align-items-center justify-content-between mt-2">
                    <span className="wv-chip">{usedInSlot.length} used</span>
                    <span className="wv-chip">{items.length} total</span>
                  </div>
                </div>
              );
            })}

            {/* No-step zone */}
            <div
              className={`wv-slot ${activeStepKey === "no-step" ? "active" : ""} ${dragOverStepKey === "no-step" ? "dropover" : ""}`}
              onClick={() => setActiveStepKey("no-step")}
              onDragOver={onDragOverStep("no-step")}
              onDrop={onDropToStep("no-step")}
              title="Drop here to remove from slot"
            >
              
              <div className="d-flex align-items-center justify-content-between mt-2">
                <span className="wv-chip">{(mediaInStep["no-step"] || []).length} threads</span>
              </div>
            </div>
          </div>

          {activeStepKey && activeStepKey !== "no-step" ? (
            <div className="wv-slot-note">
              <p className="wv-slot-note-text">{activeStepText || "No text is attached to this slot yet."}</p>
            </div>
          ) : null}

          {finalVideoUrl ? (
            <div className="mt-3">
              <div className="small mb-2" style={{ opacity: 0.8 }}>Latest export</div>
              <video className="wv-preview" src={finalVideoUrl} controls playsInline />
            </div>
          ) : null}
        </div>
      </div>
      {zoomOpen ? (
        <div className="wv-zoom-overlay" onClick={closeZoom} role="dialog" aria-modal="true">
          <div className="wv-zoom-dialog" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="wv-zoom-close" onClick={closeZoom} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>

            {zoomIsVideo ? (
              <video className="wv-zoom-media" src={zoomUrl} controls playsInline autoPlay />
            ) : (
              <img className="wv-zoom-media" src={zoomUrl} alt="" />
            )}
          </div>
        </div>
      ) : null}

    </div>
  );
}
